import { useEffect, useState, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Slider } from "../components/ui/slider";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, ArrowLeft, ChevronLeft, ChevronRight,
  Pause, Play, Volume2, VolumeX, Check, Home, TrendingDown, Settings, HelpCircle,
  Brain, Waves, Moon, CheckCircle2, Eye, Hand, BarChart3, TreePine, Speaker, Square
} from "lucide-react";
import { getSoundPrefs, setSoundPrefs, playSound, speakBreathCue, resetBreathCues, SOUND_TYPES, startSpeechKeepAlive, stopSpeechKeepAlive, primeBreathCues, playCelebrationChime, playFireworksSoundtrack } from "../lib/soundEngine";
import { getNarrationPref, setNarrationPref, playNarration, playNarrationSequence, stopNarration, unlockAudio } from "../lib/narrationEngine";
import { isLockedForUser, isTrialExpired } from "../lib/trialAccess";
import { getPracticeBySlug } from "../lib/practiceCatalog";
import { Lock } from "lucide-react";
import { trackHandsFreeEvent } from "../lib/handsFreeAnalytics";
import { getInterventionGraph, resolveNextNode } from "../lib/interventionGraphs";
import { hapticTick, hapticCelebrate } from "../lib/haptics";
import { pickMetaphor, resetMetaphorHistory } from "../lib/metaphors";
import { pickEncouragement, resetEncouragementHistory } from "../lib/encouragements";
import { CompletionSummary } from "../components/CompletionSummary";
import { AppMenu } from "../components/AppMenu";
import { useBilling } from "../contexts/BillingContext";
import { postStreakEvent } from "../lib/streak";
import {
  isGuestParam,
  writeGuestSession,
  readGuestSession,
  trackGuestStart,
  trackGuestTaskCompleted,
  trackGuestAbandonedAtTask,
  GUEST_MAX_TASK,
  GUEST_PRACTICE_TYPE,
} from "../lib/guestSession";
import { GuestUpgradeModal } from "../components/GuestUpgradeModal";
import { StepCompletionCelebration } from "../components/StepCompletionCelebration";
import { getPracticeIntro, PRACTICE_TIME_NOTE } from "../lib/practiceIntros";
import { BilateralDemo } from "../components/BilateralDemo";
import {
  NarrationChoiceModal,
  hasNarrationChoiceFor,
  saveNarrationChoiceFor,
} from "../components/NarrationChoiceModal";
import confetti from "canvas-confetti";
import logger from "../lib/logger";
import { useWakeLock } from "../lib/useWakeLock";
import { secureRandom } from "../lib/secureRandom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVENTION_TITLES = {
  quiet_the_worry: "Quiet The Worry",
  overcome_overwhelm: "Overcome Your Overwhelm",
  dial_down_tension: "Dial Down Body Tension",
  healing_old_stories: "Healing Old Stories To Let Them Go",
  beat_stage_fright: "Beat Stage Fright",
  fear_of_sleeplessness: "Fear Of Sleeplessness",
  shake_off_sadness: "Shake Off The Blues",
  boost_self_worth: "Boost Your Self-Worth",
  hold_boundaries: "Hold Your Boundaries",
  relief_from_grief: "Relief From Grief",
  calm_your_anger: "Calm Your Anger",
  enhance_performance: "Enhance Your Performance",
  new_habit_new_you: "New Habit, New You",
  headache_relief: "Headache Relief",
  nightmare_reset: "Nightmare Reset",
  fear_of_flying: "Fear Of Flying",
  cpap_claustrophobia: "CPAP Mask Claustrophobia",
  fear_of_small_places: "Fear Of Small Places",
  new_experience_anxiety: "Anxiety Of A New Experience",
  resist_an_urge: "Resist An Urge"
};

// Render a string with inline markdown-style **bold** converted into <strong>.
// Used for task content so authors can emphasize a word (e.g. **replay**).
function renderInlineBold(text) {
  if (typeof text !== "string" || !text.includes("**")) return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={idx} className="font-extrabold text-[#1B2349]">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

// Bilateral Metronome Player Component
// 114 BPM, alternating left-right, 50 right-ear beats (100 total)
// Uses sound engine for configurable tone types
const BinauralPlayer = forwardRef(({ onComplete }, ref) => {
  const { type } = useParams();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentRep, setCurrentRep] = useState(0);
  const [currentSide, setCurrentSide] = useState("left");
  const [volume, setVolume] = useState(() => getSoundPrefs().volume);
  const [voiceVolume, setVoiceVolume] = useState(() => getSoundPrefs().voiceVolume);
  const [isMuted, setIsMuted] = useState(false);
  const [soundType, setSoundType] = useState(() => getSoundPrefs().soundType);
  
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);
  const startTimeoutRef = useRef(null); // tracks the 3s lead-in timeout
  const volumeRef = useRef(volume);
  const voiceVolumeRef = useRef(voiceVolume);
  const mutedRef = useRef(false);
  const soundTypeRef = useRef(soundType);
  
  const TOTAL_RIGHT_BEATS = 50;
  const BPM = 114;
  const BEAT_INTERVAL = 60000 / BPM;

  // Sync refs
  useEffect(() => { volumeRef.current = volume; }, [volume]);
  useEffect(() => { voiceVolumeRef.current = voiceVolume; }, [voiceVolume]);
  useEffect(() => { mutedRef.current = isMuted; }, [isMuted]);
  useEffect(() => { soundTypeRef.current = soundType; }, [soundType]);

  // Re-read prefs when component mounts (in case user changed in settings)
  useEffect(() => {
    const prefs = getSoundPrefs();
    setVolume(prefs.volume);
    setVoiceVolume(prefs.voiceVolume);
    setSoundType(prefs.soundType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOrCreateContext = useCallback(() => {
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  const startPlaying = useCallback(() => {
    // Kick off the breath-cue audio context + preload the first clip inside
    // the click handler so iOS Safari lets us play audio later and the first
    // session has zero playback latency.
    primeBreathCues();
    const ctx = getOrCreateContext();
    setIsPlaying(true);
    setCurrentRep(0);
    setCurrentSide("left");

    // Prime speech synthesis + start the keep-alive so the end-of-session
    // voice cue fires reliably. No-ops now that breath cues use real audio,
    // but kept here as harmless legacy — will be removed with the next cleanup.

    let beatIndex = 0;
    let rightCount = 0;

    // Brief 3-second lead-in before the first beat so the user has a moment
    // to settle and place their attention. Stored on the ref so stopPlaying
    // can cancel it if the user taps Pause during the lead-in.
    startTimeoutRef.current = setTimeout(() => {
      startTimeoutRef.current = null;
      const vol = mutedRef.current ? 0 : volumeRef.current;
      playSound(ctx, soundTypeRef.current, "left", vol);

      intervalRef.current = setInterval(() => {
        beatIndex++;
        const side = beatIndex % 2 === 0 ? "left" : "right";
        const v = mutedRef.current ? 0 : volumeRef.current;

        if (side === "right") {
          rightCount++;
          setCurrentRep(rightCount);
        }
        setCurrentSide(side);
        playSound(ctx, soundTypeRef.current, side, v);

        if (rightCount >= TOTAL_RIGHT_BEATS) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          setIsPlaying(false);
          // Gentle spoken cue at the end of the bilateral sound — now uses
          // prerecorded voice clips rather than browser TTS, picked randomly
          // without repeats. Volume scales with the bilateral slider.
          // Delay leaves a clear breath of silence between the last bilateral
          // beat and the spoken cue. We await the cue's `ended` event before
          // firing onComplete so any UI tied to "the bilateral session is
          // done" (e.g. the floating distress check-in modal) appears AFTER
          // the voice has finished — not on top of it.
          setTimeout(async () => {
            const vv = mutedRef.current ? 0 : voiceVolumeRef.current;
            await speakBreathCue(vv);
            if (onComplete) onComplete();
          }, 1800);
        }
      }, BEAT_INTERVAL);
    }, 3000);
  }, [getOrCreateContext, onComplete, BEAT_INTERVAL, TOTAL_RIGHT_BEATS]);

  const stopPlaying = useCallback(() => {
    setIsPlaying(false);
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopPlaying();
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, [stopPlaying]);

  // Expose just enough to the parent so the narration speaker button
  // can pause the bilateral tone before it starts narrating, and
  // hands-free mode can launch the tone after narration ends.
  useImperativeHandle(ref, () => ({
    isPlaying: () => isPlaying,
    stop: stopPlaying,
    start: startPlaying,
  }), [isPlaying, stopPlaying, startPlaying]);

  const soundLabel = SOUND_TYPES.find(s => s.id === soundType)?.name || "Wooden Click";

  const progressPercent = (currentRep / TOTAL_RIGHT_BEATS) * 100;

  return (
    <div className="binaural-player p-6 rounded-2xl text-white" data-testid="binaural-player">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-white/70">Bilateral Metronome &middot; {soundLabel}</p>
          <p className="text-lg font-medium">114 BPM &middot; {TOTAL_RIGHT_BEATS} cycles</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/settings?returnTo=/intervention/${type}`}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="player-settings-btn"
            title="Sound settings"
          >
            <Settings className="w-4 h-4 text-white/70" />
          </Link>
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="mute-btn"
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={isPlaying ? stopPlaying : startPlaying}
          className="w-14 h-14 bg-[#3090A8] hover:bg-[#7A9677] rounded-full flex items-center justify-center transition-colors"
          data-testid="play-pause-btn"
        >
          {isPlaying ? (
            <Pause className="w-6 h-6" />
          ) : (
            <Play className="w-6 h-6 ml-1" />
          )}
        </button>
        
        <div className="flex-1">
          <div className="flex items-center justify-between text-sm mb-2">
            <span>Cycle {currentRep} of {TOTAL_RIGHT_BEATS}</span>
            <span>{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2 bg-white/20" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Volume2 className="w-4 h-4 text-white/70" />
        <Slider
          value={[volume * 100]}
          onValueChange={(val) => {
            const v = val[0] / 100;
            setVolume(v);
            // Persist so the change survives the session — otherwise next
            // intervention or page reload would snap back to the saved prefs.
            setSoundPrefs({ ...getSoundPrefs(), volume: v });
          }}
          max={100}
          step={1}
          className="flex-1"
          data-testid="volume-slider"
        />
      </div>

      {isPlaying && (
        <p className="text-center text-sm text-white/70 mt-4 animate-pulse-slow">
          Keep moving your eyes or tapping your legs...
        </p>
      )}
    </div>
  );
});
BinauralPlayer.displayName = "BinauralPlayer";

// Distress Level Checker Component (mobile-friendly slider)
const DistressChecker = ({ label, value, onChange, testId, hideLabel = false }) => {
  const colors = [
    "#3090A8", "#3090A8", "#9AB898", // 0-2 green (low)
    "#B8C88A", "#D4D06A", "#E2B887", // 3-5 yellow-orange (moderate)
    "#D8A070", "#D08060", "#D98880", // 6-8 orange-red (high)
    "#C06060", "#B04040" // 9-10 red (severe)
  ];

  const currentValue = value ?? 0;
  const hasValue = value !== null && value !== undefined;
  const activeColor = colors[currentValue];

  const severityLabel =
    currentValue <= 2 ? "Minimal distress" :
    currentValue <= 5 ? "Moderate distress" :
    currentValue <= 8 ? "High distress" : "Severe distress";

  // Gentle haptic tick whenever the distress value actually changes.
  // Delegated to the haptics helper so native Capacitor iOS gets real haptics,
  // web Android gets navigator.vibrate, and iOS Safari silently no-ops.
  const handleChange = (newValue) => {
    if (newValue !== value) hapticTick();
    onChange(newValue);
  };

  return (
    <div
      className="bg-[#FBF8F1] rounded-2xl p-5 border border-slate-100"
      data-testid={testId}
    >
      {!hideLabel && (
        <div className="flex items-start gap-2 mb-4 justify-center">
          <TrendingDown className="w-4 h-4 text-[#78C0D8] mt-0.5 flex-shrink-0" />
          <p className="text-sm font-medium text-[#1B2349] text-center">{label}</p>
        </div>
      )}

      {/* Big current value display */}
      <div className="flex flex-col items-center mb-4">
        <div
          className="w-20 h-20 rounded-2xl flex items-center justify-center font-outfit font-semibold text-4xl text-white shadow-sm transition-colors"
          style={{ backgroundColor: hasValue ? activeColor : "#CBD5CB" }}
          data-testid={`${testId}-current-value`}
        >
          {hasValue ? currentValue : "—"}
        </div>
        {hasValue && (
          <p
            className="text-sm mt-2 font-medium"
            style={{ color: activeColor }}
            data-testid={`${testId}-severity`}
          >
            {severityLabel}
          </p>
        )}
      </div>

      {/* Slider */}
      <div className="px-1">
        <Slider
          value={[currentValue]}
          onValueChange={(vals) => handleChange(vals[0])}
          min={0}
          max={10}
          step={1}
          aria-label={label}
          data-testid={`${testId}-slider`}
          className="w-full distress-slider"
          style={{ "--distress-color": activeColor }}
        />
        {/* Number tap targets. Each number is a real button that jumps
            the slider to that value on tap — critical for accessibility
            on iOS where fine-tuning a drag thumb between 0-10 is
            imprecise. min-height 44px matches Apple HIG's tap-target
            recommendation; -mt-1 pulls the row up so it visually reads
            as scale ticks rather than a separate control block. */}
        <div className="flex justify-between mt-1 px-0.5">
          {[0,1,2,3,4,5,6,7,8,9,10].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => handleChange(n)}
              className={`min-w-[36px] min-h-[44px] flex items-center justify-center rounded-lg text-sm tabular-nums cursor-pointer transition-colors active:bg-slate-100 ${
                hasValue && n === currentValue
                  ? "font-semibold text-[#1B2349]"
                  : "text-[#718096] hover:text-[#1B2349]"
              }`}
              style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              aria-label={`Set ${label.toLowerCase()} to ${n}`}
              data-testid={`${testId}-tick-${n}`}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="flex justify-between mt-1 text-sm font-medium text-[#718096]">
          <span>None</span>
          <span>Worst</span>
        </div>
      </div>
    </div>
  );
};

// ============ GETTING STARTED TASK BODY ============
// In-flow "Getting Started" content used as task 1 for every intervention.
// Styled as a single task card (same visual shell as tasks 2-9) rather than a
// marketing-style landing page — so it reads as the first beat of a guided
// session, not an app homepage.
const GettingStartedTaskContent = ({ narrationOn, onChangeNarrationInSettings }) => (
  <div className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 animate-fade-in-up overflow-hidden">
    {/* Accent gradient strip — matches tasks 2-9 so Task 1 belongs to the same card family */}
    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#3090A8] via-[#78C0D8] to-[#E2B887] rounded-t-2xl" />

    {/* Eyebrow + title — orients the user to where they are in the flow */}
    <p className="text-xs font-medium text-[#3090A8] uppercase tracking-[0.14em] mb-2 pt-2">
      Before you begin
    </p>
    <h2 className="font-outfit font-semibold text-[1.375rem] sm:text-2xl text-[#1B2349] leading-tight mb-4">
      How Bilateral Calm works
    </h2>
    <p className="text-[#1B2349] leading-relaxed mb-8">
      Take a minute to get familiar with the method. The rest of the session will feel natural once you have the shape of it in your mind.
    </p>

    {/* How it works — three quiet inline beats */}
    <div className="mb-8">
      <h3 className="font-outfit font-medium text-[1.0625rem] sm:text-base text-[#1B2349] mb-4">The three beats of a session</h3>
      <ol className="space-y-4">
        <li className="flex gap-4 items-start">
          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E9F0E8] text-[#3090A8] flex items-center justify-center font-outfit font-semibold text-sm">1</span>
          <div>
            <p className="font-outfit font-medium text-[#1B2349] mb-0.5">Hold a feeling or body sensation</p>
            <p className="text-[#4A5568] leading-relaxed">Bring to mind whatever's weighing on you — a worry, a memory, a tightness in the body. Just notice it.</p>
          </div>
        </li>
        <li className="flex gap-4 items-start">
          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#EAF0F9] text-[#78C0D8] flex items-center justify-center font-outfit font-semibold text-sm">2</span>
          <div>
            <p className="font-outfit font-medium text-[#1B2349] mb-0.5">Follow the tone</p>
            <p className="text-[#4A5568] leading-relaxed">A gentle bilateral tone bounces left and right between your ears to help you time the bilateral movement. Let your eyes or hands follow along. If moving your eyes is too distracting, simply shift to tapping your thighs; this is often easier and just as effective. (More on this below)</p>
          </div>
        </li>
        <li className="flex gap-4 items-start">
          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FEF3E7] text-[#B8965A] flex items-center justify-center font-outfit font-semibold text-sm">3</span>
          <div>
            <p className="font-outfit font-medium text-[#1B2349] mb-0.5">Your mind settles</p>
            <p className="text-[#4A5568] leading-relaxed">Much like it does in deep REM sleep, your mind begins to soothe itself and the tension loosens its grip.</p>
          </div>
        </li>
      </ol>
    </div>

    {/* Science — kept as a quiet inline passage rather than its own card */}
    <div className="mb-8 pl-4 border-l-2 border-[#78C0D8]/40">
      <p className="text-[#1B2349] leading-relaxed mb-3">
        Bilateral stimulation (shifting between the right and left sides) is really quite simple. The idea is to gently get your brain switching back and forth between its left and right sides while you hold a problem or stressful feeling in mind. When that happens, your brain kicks into a natural calming process, very similar to what it does on its own during deep REM sleep.
      </p>
      <p className="text-[#1B2349] leading-relaxed">
        Science has shown time and again that pairing this left-right brain activity with a stressful thought or feeling takes the edge off — easing tension in the body, softening the emotion, and causing the problem to lose its grip.
      </p>
    </div>

    {/* Two methods — compact inline, no heavy card treatment */}
    <div>
      <h3 className="font-outfit font-medium text-[1.0625rem] sm:text-base text-[#1B2349] mb-4">Pick a way to follow along with the bilateral sound</h3>

      {/* Visual demonstration — Tap Play to see eye movement / hand tapping */}
      <BilateralDemo />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-100 bg-[#FAFBFC] p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[#EAF0F9] rounded-lg flex items-center justify-center">
              <Eye className="w-4.5 h-4.5 text-[#78C0D8]" />
            </div>
            <h4 className="font-outfit font-medium text-[#1B2349]">Eye Movement</h4>
          </div>
          <p className="text-[#4A5568] leading-relaxed">
            Picture yourself sitting on the center line of a tennis court, head perfectly still, watching the ball bounce side to side. Let your eyes sweep smoothly from left to right and back again — without moving your head.
          </p>
        </div>

        <div className="rounded-xl border border-slate-100 bg-[#FAFBFC] p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[#FEF3E7] rounded-lg flex items-center justify-center">
              <Hand className="w-4.5 h-4.5 text-[#B8965A]" />
            </div>
            <h4 className="font-outfit font-medium text-[#1B2349]">Hand Tapping</h4>
          </div>
          <p className="text-[#4A5568] leading-relaxed">
            Rest your palms on your thighs with your legs uncrossed, then gently tap left and right in time with the tone. You're welcome to close your eyes during the tapping.
          </p>
        </div>
      </div>
      <p className="text-sm text-[#718096] mt-3 flex items-center gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5 text-[#3090A8]" />
        Either method works — pick whichever feels easier.
      </p>
    </div>

    {/* Narration status — surfaces the choice the user just made in the
        opt-in modal, and gives them a way to change it via Settings. */}
    <div
      className="mt-8 rounded-xl border border-slate-100 bg-[#FAFBFC] p-4 flex items-center gap-3"
      data-testid="narration-status-row"
    >
      <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
        <Speaker className={`w-4 h-4 ${narrationOn ? "text-[#3090A8]" : "text-[#A0AEC0]"}`} />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-[#1B2349]">
          Narration is <span className={narrationOn ? "text-[#3090A8]" : "text-[#718096]"}>{narrationOn ? "on" : "off"}</span>
        </p>
        <p className="text-xs text-[#718096] leading-relaxed mt-0.5">
          {narrationOn
            ? "Each task will play automatically when you move forward."
            : "Read each task at your own pace — tap the speaker icon to listen any time."}
        </p>
      </div>
      <button
        type="button"
        onClick={onChangeNarrationInSettings}
        className="text-xs text-[#3090A8] font-medium hover:underline whitespace-nowrap"
        data-testid="narration-status-settings-link"
      >
        Change in Settings
      </button>
    </div>
  </div>
);

// ============ TASK 1A — GOAL + CHECK-IN EXPLANATION ============
// Second "getting started" page. Mirrors the visual shell of task-1 (same
// accent strip, eyebrow + title, numbered beats) so the first two screens
// feel like a cohesive two-part intro rather than a homepage then a prompt.
const GoalAndCheckInContent = () => (
  <div className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 animate-fade-in-up overflow-hidden">
    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#3090A8] via-[#78C0D8] to-[#E2B887] rounded-t-2xl" />

    <p className="text-xs font-medium text-[#3090A8] uppercase tracking-[0.14em] mb-2 pt-2">
      A quick orientation
    </p>
    <h2 className="font-outfit font-semibold text-[1.375rem] sm:text-2xl text-[#1B2349] leading-tight mb-4">
      How we'll know it's working
    </h2>
    <p className="text-[#1B2349] leading-relaxed mb-8">
      Before you begin, here's the simple goal — and what to expect along the way.
    </p>

    <div className="mb-8">
      <h3 className="font-outfit font-medium text-[1.0625rem] sm:text-base text-[#1B2349] mb-4">What to expect</h3>
      <ol className="space-y-4">
        <li className="flex gap-4 items-start">
          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F1ECE0] text-[#1B2349] flex items-center justify-center font-outfit font-semibold text-sm">1</span>
          <div>
            <p className="font-outfit font-medium text-[#1B2349] mb-0.5">You'll have a task at each step</p>
            <p className="text-[#4A5568] leading-relaxed">It might be to be aware of a body experience, to think of the problem in a specific way, or a suggestion to change your perspective on it.</p>
          </div>
        </li>
        <li className="flex gap-4 items-start">
          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E9F0E8] text-[#3090A8] flex items-center justify-center font-outfit font-semibold text-sm">2</span>
          <div>
            <p className="font-outfit font-medium text-[#1B2349] mb-0.5">Aim for 0 or 1</p>
            <p className="text-[#4A5568] leading-relaxed">When first working with your problem, you will likely feel your tension level rise.<br /><br />From there, the goal is to bring your overall mind and body tension down to a 0 or 1 on a 0–10 scale. You will be guided during each step. That's it.</p>
          </div>
        </li>
        <li className="flex gap-4 items-start">
          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#EAF0F9] text-[#78C0D8] flex items-center justify-center font-outfit font-semibold text-sm">3</span>
          <div>
            <p className="font-outfit font-medium text-[#1B2349] mb-0.5">Check in after each round</p>
            <p className="text-[#4A5568] leading-relaxed">After every round of bilateral stimulation using eye movements or hand tapping, you'll move a slider to show your tension level.</p>
          </div>
        </li>
        <li className="flex gap-4 items-start">
          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FEF3E7] text-[#B8965A] flex items-center justify-center font-outfit font-semibold text-sm">4</span>
          <div>
            <p className="font-outfit font-medium text-[#1B2349] mb-0.5">Still above a 1? We'll keep going</p>
            <p className="text-[#4A5568] leading-relaxed">If your tension is still above a 1, you'll get another round — along with gentle tips to shift perspective and let the tension ease.</p>
          </div>
        </li>
      </ol>
    </div>

    <div className="pl-4 border-l-2 border-[#78C0D8]/40">
      <p className="text-[#1B2349] leading-relaxed italic">
        Reaching 0 or 1 can take several rounds — and that's completely normal. Each pass does a little more work. There's no rush.
      </p>
    </div>
  </div>
);

// ============ TASK 9 CLOSING VIEW ============
// Editorial/journal-letter layout — narrower single column, oversized
// decorative pull-quote, margin-rule paragraphs, and tiny ornamental dividers.
// Reads as a warm closing letter rather than a dashboard of cards.
const Task9Content = ({ task }) => {
  const raw = task.content || "";
  const [beforePart, afterPart] = raw.includes("{{CELEBRATION}}")
    ? raw.split("{{CELEBRATION}}")
    : [raw, ""];
  const openingParas = beforePart.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  const takeawayParas = afterPart.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  const [opener, ...leadingExtras] = openingParas.length > 0 ? openingParas : [""];
  const takeaways = [...leadingExtras, ...takeawayParas];

  // A small decorative glyph rendered between sections.
  const Glyph = () => (
    <div className="flex items-center justify-center my-10" aria-hidden="true">
      <span className="h-px w-16 bg-[#D9E1D8]" />
      <span className="mx-3 text-[#3090A8] text-sm tracking-[0.5em]">✦</span>
      <span className="h-px w-16 bg-[#D9E1D8]" />
    </div>
  );

  return (
    <div
      className="relative bg-white rounded-[28px] border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] overflow-hidden animate-fade-in-up"
      data-testid="task-9-content"
    >
      {/* Soft layered background wash — subtle warmth without loud gradients */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-[#FAFBFA] to-[#F7FAF7] pointer-events-none" />
      <div className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-[#3090A8]/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-[#78C0D8]/8 blur-3xl pointer-events-none" />

      {/* Narrower centered column — reads like a page in a quiet journal */}
      <div className="relative max-w-[620px] mx-auto px-6 sm:px-10 py-12 sm:py-14">
        {/* Eyebrow + title — hushed, no big colored hero */}
        <div className="text-center">
          <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.28em] text-[#3090A8] font-medium mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            Session complete
          </p>
          <h2
            className="font-outfit font-semibold text-3xl sm:text-4xl text-[#1B2349] leading-tight"
            data-testid="task-9-title"
          >
            {task.title}
          </h2>
        </div>

        {/* Decorative pull-quote for the celebration line */}
        {task.celebration && (
          <figure className="mt-10 text-center" data-testid="celebration-line">
            <span
              aria-hidden="true"
              className="block text-[#3090A8]/40 font-serif text-6xl leading-none mb-2 select-none"
            >
              &ldquo;
            </span>
            <blockquote className="font-outfit italic text-lg sm:text-xl text-[#1B2349] leading-relaxed px-2">
              {task.celebration}
            </blockquote>
          </figure>
        )}

        <Glyph />

        {/* A Final Check-in — framed as a reflective prompt, not a card */}
        {opener && (
          <section>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#78C0D8] font-medium mb-3 text-center">
              A final check-in
            </p>
            <p className="text-[#1B2349] leading-[1.8] text-center italic whitespace-pre-line">
              {opener}
            </p>
          </section>
        )}

        {takeaways.length > 0 && <Glyph />}

        {/* Body paragraphs — each with a soft sage margin rule on the left so the
            whole section reads like a journal entry rather than stacked cards */}
        {takeaways.map((para, idx) => (
          <article
            key={idx}
            className="pl-5 border-l-2 border-[#3090A8]/30 mb-8 last:mb-0"
            data-testid={`task-9-paragraph-${idx}`}
          >
            <p className="text-[#1B2349] leading-[1.8] whitespace-pre-line">
              {para}
            </p>
          </article>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Auto-narration scripts for Task 1 and Task 1a
// ----------------------------------------------------------------------------
// Tasks 1 and 1a render through their own custom JSX (not task.content),
// so we hand-author the narration text to mirror exactly what's on screen.
// Blank lines = paragraph breaks (1.5 s natural pause from the TTS engine).
// ============================================================================
const TASK_1_NARRATION = `Before you begin.

How Bilateral Calm works.

Take a minute to get familiar with the method. The rest of the session will feel natural once you have the shape of it in your mind.

The three beats of a session.

One. Hold a feeling or body sensation. Bring to mind whatever's weighing on you — a worry, a memory, a tightness in the body. Just notice it.

Two. Follow the tone. A gentle bilateral tone bounces left and right between your ears to help you time the bilateral movement. Let your eyes or hands follow along. If moving your eyes is too distracting, simply shift to tapping your thighs; this is often easier and just as effective.

Three. Your mind settles. Much like it does in deep REM sleep, your mind begins to soothe itself and the tension loosens its grip.

Bilateral stimulation, shifting between the right and left sides, is really quite simple. The idea is to gently get your brain switching back and forth between its left and right sides while you hold a problem or stressful feeling in mind. When that happens, your brain kicks into a natural calming process, very similar to what it does on its own during deep REM sleep.

Science has shown time and again that pairing this left-right brain activity with a stressful thought or feeling takes the edge off — easing tension in the body, softening the emotion, and causing the problem to lose its grip.

Pick a way to follow along with the bilateral sound.

Eye movement. Picture yourself sitting on the center line of a tennis court, head perfectly still, watching the ball bounce side to side. Let your eyes sweep smoothly from left to right and back again — without moving your head.

Hand tapping. Rest your palms on your thighs with your legs uncrossed, then gently tap left and right in time with the tone. You're welcome to close your eyes during the tapping.

Either method works — pick whichever feels easier.`;

const TASK_1A_NARRATION = `How we'll know it's working.

Before you begin, here's the simple goal — and what to expect along the way.

What to expect.

One. You'll have a task at each step. It might be to be aware of a body experience, to think of the problem in a specific way, or a suggestion to change your perspective on it.

Two. Aim for 0 or 1. When first working with your problem, you will likely feel your tension level rise. From there, the goal is to bring your overall mind and body tension down to a 0 or 1 on a 0–10 scale. You will be guided during each step. That's it.

Three. Check in after each round. After every round of bilateral stimulation using eye movements or hand tapping, you'll move a slider to show your tension level.

Four. Still above a one? We'll keep going. If your tension is still above a one, you'll get another round — along with gentle tips to shift perspective and let the tension ease.

Reaching 0 or 1 can take several rounds — and that's completely normal. Each pass does a little more work. There's no rush.`;

// Main Intervention Flow Component
const InterventionFlow = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Guest mode: cold visitors can try the first 4 tasks of
  // dial_down_body_tension without an account. When `isGuest` is true
  // we (a) skip auth-required API calls and persist progress to
  // localStorage instead, (b) hard-block at Task 4 with the upgrade
  // modal, (c) fire GA4 funnel events. See lib/guestSession.js.
  const isGuest = !user && isGuestParam();
  const [showGuestUpgrade, setShowGuestUpgrade] = useState(false);
  // When set (to the number of the task just finished), we render the
  // celebration overlay instead of navigating immediately. Cleared when
  // the user hits Continue (advance) or Pause (exit).
  const [pendingCelebrationTask, setPendingCelebrationTask] = useState(null);

  // Trial access gating. Pulled from the shared BillingContext instead
  // of a local fetch so intervention pages don't duplicate the
  // `/api/billing/status` request that BillingProvider already made on
  // sign-in. Renders a paywall view below when locked.
  const { billing, loading: billingContextLoading } = useBilling();
  const billingStatus = billing?.status || null;
  const billingLoaded = !billingContextLoading;
  const isLocked = isLockedForUser(type, billingStatus);
  
  // Keep the screen awake while the user is inside an unlocked practice.
  // Practices are audio-heavy and users often go hands-free, so the OS
  // dimming or locking the screen breaks the flow (and can suspend
  // background audio on iOS). Silently no-ops on unsupported browsers.
  useWakeLock(!isLocked && billingLoaded);

  const [progress, setProgress] = useState(null);
  const [currentNodeId, setCurrentNodeId] = useState("task-1");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taskInput, setTaskInput] = useState("");
  const [distressAfter, setDistressAfter] = useState(null);
  // When the bilateral player completes on a node that asks for a distress
  // check-in or Yes/No answer, we surface a floating forced-choice modal
  // instead of inline controls. This decl must precede any reads.
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [answerYes, setAnswerYes] = useState(null);  // null | true | false
  const [nodeHistory, setNodeHistory] = useState([]); // breadcrumb stack of visited node IDs
  const [playCount, setPlayCount] = useState(0);
  const [milestoneCount, setMilestoneCount] = useState(0); // total celebrations this session
  const [celebratingSud, setCelebratingSud] = useState(null); // 0 | 1 — controls inline pill
  const celebratedNodesRef = useRef(new Set()); // legacy — kept for step-indicator logic
  const celebratedTasksRef = useRef(new Set()); // one confetti burst per task stage (1-9)
  const [showNotImprovingSuggestion, setShowNotImprovingSuggestion] = useState(false);
  const [showCompletionSummary, setShowCompletionSummary] = useState(false);
  const completionShownRef = useRef(false);

  // "Is this practice right for you?" intro card — shown the very first time
  // a user opens this intervention (task-1, no previous progress). Once
  // dismissed via "Let's get started" it never shows again for this user +
  // intervention (tracked in localStorage so it persists across sessions).
  const introStorageKey = `bc:practice-intro-seen:${type}`;
  const [showIntro, setShowIntro] = useState(false);

  // Per-node visit tally — used to surface fresh metaphor imagery on loop-back
  // branch nodes (A, A1, B, C, E, F). Being on any of these nodes means the user
  // didn't pass the prior distress gate, so this IS a repeat attempt — show a
  // fresh metaphor every time to keep the experience from feeling stale.
  // `navSeq` bumps on every navigation (even self-loops like A1 → A1), so the
  // metaphor-picking effect re-runs when a user loops onto the same node.
  const visitCountsRef = useRef({});
  const [currentMetaphor, setCurrentMetaphor] = useState(null);
  const [navSeq, setNavSeq] = useState(0);
  const METAPHOR_NODES = ["A", "A1", "B", "C", "E", "F"];

  const graph = getInterventionGraph(type);
  const node = graph?.nodes?.[currentNodeId];
  const currentTask = node?.taskNumber || 1;

  // Substitute the {{ENCOURAGE}} token with a non-repeating random phrase
  // from the encouragement pool. Recomputed every time the user lands on a
  // new node (keyed on currentNodeId + navSeq) so each task visit gets a
  // fresh phrase; stable across re-renders of the same visit so it doesn't
  // flicker on typing/distress slider interactions.
  // Hook is intentionally placed BEFORE any early returns so rules-of-hooks
  // stay satisfied even when `node` is momentarily null.
  const task = useMemo(() => {
    if (!node) return node;
    const raw = node.content || "";
    if (!raw.includes("{{ENCOURAGE}}")) return node;
    return { ...node, content: raw.replace("{{ENCOURAGE}}", pickEncouragement()) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNodeId, navSeq]);

  // ---- Task narration ("read it aloud") --------------------------------
  // Spoken-word TTS that the user can trigger from a small speaker button
  // next to the task title. Persists the user's "narrate automatically"
  // preference across sessions via narrationEngine.
  const binauralPlayerRef = useRef(null);
  const [narrationState, setNarrationState] = useState("idle"); // 'idle' | 'loading' | 'playing'
  const [narrationAuto, setNarrationAuto] = useState(() => getNarrationPref());
  // Per-practice narration opt-in modal. Shown the first time the user
  // opens THIS practice (regardless of their global narrate-tasks pref)
  // so they consciously choose whether the calm voice should auto-play
  // each task. Choice is persisted under bc_narration_choice_<slug>.
  const [showNarrationModal, setShowNarrationModal] = useState(false);
  // Direction the user last moved through the flow. Auto-narration only
  // fires on forward navigation — going back to a previous task should
  // stay silent (per req 4). Reset to "forward" after each backward
  // navigation is consumed by the auto-narrate effect.
  const lastNavDirectionRef = useRef("forward");
  // Hands-free ("Play all") mode — orchestrates narration → bilateral
  // tone → next-task chain so the user can move through a whole practice
  // without touching the screen (until a slider/text input is required).
  // Keys: 'off' | 'running' | 'paused' | 'waiting-for-input' | 'waiting-for-tone-complete'.
  const [handsFreeState, setHandsFreeState] = useState("off");
  const handsFreeRef = useRef("off");
  useEffect(() => { handsFreeRef.current = handsFreeState; }, [handsFreeState]);

  // Global gesture listener that re-primes the audio element on every
  // user tap while the intervention is on-screen. iOS Safari/Capacitor
  // occasionally re-locks audio after long idle periods (e.g. the user
  // reads a task for 60 s before tapping Continue), so we can't rely on
  // the initial unlock alone. This runs `unlockAudio()` synchronously
  // inside each pointerdown — the safest place, before any state
  // updates or fetches can consume the gesture window. It's a cheap
  // no-op when the audio element is already active.
  useEffect(() => {
    const prime = () => unlockAudio();
    document.addEventListener("pointerdown", prime, { passive: true, capture: true });
    document.addEventListener("touchstart", prime, { passive: true, capture: true });
    return () => {
      document.removeEventListener("pointerdown", prime, { capture: true });
      document.removeEventListener("touchstart", prime, { capture: true });
    };
  }, []);
  // Keep the auto-pref in sync with localStorage when the user toggles it
  // from the Settings page in another tab / window.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "bc_narrate_tasks") setNarrationAuto(getNarrationPref());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Task 1 ("Getting Started") and Task 1a ("How we'll know it's
  // working") render via their own custom JSX (the GettingStartedTaskContent
  // and GoalAndCheckInContent components) rather than from task.content.
  // For auto-narration on those pages we use the hard-coded scripts
  // below — they mirror what's on screen line-for-line so the narrator
  // never reads anything that isn't visible to the user.
  const isTaskNarratable = Boolean(
    task?.title
      && task?.content
      && currentNodeId !== "task-1"
      && currentNodeId !== "task-1a"
  );

  // Monotonic counter — bumped on every navigation so any in-flight
  // narration fetch from the previous task knows to bail out instead
  // of stomping on the new task's audio. Without this, a slow cold
  // TTS call from task-2 can resolve AFTER the user has moved to
  // task-3, and the response would replace task-3's narration with
  // task-2's clips — exactly the "narrator reading a different
  // version" bug the user reported.
  const narrationSeqRef = useRef(0);

  const playTaskNarration = useCallback(async ({ onEnd, extraContent } = {}) => {
    // If extraContent is supplied (e.g. input prompt + metaphor text)
    // we append it to task.content so the narration covers BOTH the
    // task body AND whatever's in the input area. We deliberately
    // double-newline the join so the backend treats it as a separate
    // paragraph with the standard pause before it.
    const composedContent = extraContent
      ? `${task?.content || ""}\n\n${extraContent}`.trim()
      : task?.content;
    if (!task || !task.title || !composedContent) {
      if (typeof onEnd === "function") onEnd({ reason: "no-content" });
      return;
    }
    // Capture the navigation generation so a slow round-trip from the
    // previous task can't race the user's new task.
    const seq = ++narrationSeqRef.current;
    // If the bilateral tone is mid-play, pause it first so the narrator
    // is heard cleanly (per product spec — narration takes precedence).
    if (binauralPlayerRef.current?.isPlaying?.()) {
      binauralPlayerRef.current.stop();
    }
    setNarrationState("loading");
    try {
      const res = await axios.post(`${API}/narration/task`, {
        title: task.title,
        content: composedContent,
      });
      // Race-guard: if the user has moved on since we kicked off, drop
      // the response on the floor rather than playing stale audio.
      if (seq !== narrationSeqRef.current) {
        if (typeof onEnd === "function") onEnd({ reason: "superseded" });
        return;
      }
      const clips = Array.isArray(res?.data?.clips) ? res.data.clips : [];
      // Keep the per-clip pause_after_ms so the engine inserts the
      // right gap after each sentence vs. paragraph.
      const playable = clips.filter((c) => c && c.url);
      const pauseMs = typeof res?.data?.pause_ms === "number" ? res.data.pause_ms : 1500;
      if (!playable.length) throw new Error("missing narration clips");
      setNarrationState("playing");
      playNarrationSequence(playable, {
        pauseMs,
        onEnd: (info) => {
          // Another race-guard for the playback callback.
          if (seq !== narrationSeqRef.current) return;
          setNarrationState("idle");
          if (info?.reason === "blocked") {
            // iOS autoplay window closed while TTS was fetching. Prompt
            // rather than error — tapping the speaker will re-arm the
            // audio element via the document-level pointerdown listener.
            toast("Tap the speaker button to hear this task.", { duration: 4000 });
          } else if (info?.reason === "error") {
            toast.error("Narration playback failed. Please try again.");
          }
          if (typeof onEnd === "function") onEnd(info);
        },
      });
    } catch (err) {
      if (seq !== narrationSeqRef.current) return; // moved on already
      setNarrationState("idle");
      logger.warn("narration fetch failed", err);
      const isTimeout = err?.code === "ECONNABORTED";
      toast.error(
        isTimeout
          ? "Narration is taking too long. Check your connection or read the task instead."
          : "Couldn't load narration. Please try again."
      );
      if (typeof onEnd === "function") onEnd({ reason: "fetch-error" });
    }
  }, [task]);

  const stopTaskNarration = useCallback(() => {
    stopNarration();
    setNarrationState("idle");
  }, []);

  // Build the extra narratable content (input prompt + metaphor) for
  // the current task. Returns a non-empty string when the task has a
  // text input OR is showing a metaphor line; otherwise null. Kept as
  // a stable function so all narration entry points use the same
  // composition.
  const currentMetaphorRef = useRef("");
  useEffect(() => { currentMetaphorRef.current = currentMetaphor || ""; }, [currentMetaphor]);
  const buildExtraNarrationContent = useCallback(() => {
    const bits = [];
    if (task?.inputPrompt) bits.push(task.inputPrompt);
    if (task?.hasInput && currentMetaphorRef.current) {
      bits.push(currentMetaphorRef.current);
    }
    return bits.join("\n\n");
  }, [task]);

  // Speak an arbitrary block of text (used for the "How did that feel?"
  // check-in modal and the distress-slider prompt at the end of a
  // hands-free task). Same paragraph-by-paragraph chained backend so
  // multi-sentence prompts get the same 1.5s pause between sections.
  const narrateString = useCallback(async (text, { onEnd, title } = {}) => {
    if (!text) {
      if (typeof onEnd === "function") onEnd({ reason: "no-content" });
      return;
    }
    if (binauralPlayerRef.current?.isPlaying?.()) {
      binauralPlayerRef.current.stop();
    }
    setNarrationState("loading");
    try {
      const res = await axios.post(`${API}/narration/task`, {
        // Empty title → backend skips the leading title paragraph so
        // the narration reads exactly what the caller sent, nothing
        // more. Callers that DO want a spoken heading can pass one.
        title: title || "",
        content: text,
      });
      const clips = Array.isArray(res?.data?.clips) ? res.data.clips : [];
      const playable = clips.filter((c) => c && c.url);
      const pauseMs = typeof res?.data?.pause_ms === "number" ? res.data.pause_ms : 1500;
      if (!playable.length) throw new Error("missing narration clips");
      setNarrationState("playing");
      playNarrationSequence(playable, {
        pauseMs,
        onEnd: (info) => {
          setNarrationState("idle");
          if (typeof onEnd === "function") onEnd(info);
        },
      });
    } catch (err) {
      setNarrationState("idle");
      logger.warn("narrate-string fetch failed", err);
      if (typeof onEnd === "function") onEnd({ reason: "fetch-error" });
    }
  }, []);

  // Auto-narrate whenever the user lands on a new task IF they've opted
  // in via the Settings toggle. We hash on currentNodeId so this fires
  // exactly once per task arrival. Skip if hands-free mode is running —
  // hands-free has its own narration orchestrator that needs the onEnd
  // callback wired through.
  useEffect(() => {
    if (handsFreeRef.current !== "off") return;
    if (!narrationAuto) return;
    // Don't start narration while the per-practice opt-in modal is up —
    // we wait for the user to pick.
    if (showNarrationModal) return;
    // Wait for the "How this helps" practice-intro card to be dismissed
    // before the voice begins — let the user read the framing first.
    if (showIntro) return;
    // Backward navigation must stay silent. Consume the direction flag
    // so the next forward step re-enables auto-narration. Per req 4.
    if (lastNavDirectionRef.current === "backward") {
      lastNavDirectionRef.current = "forward";
      return;
    }
    // Task 1 and Task 1a render custom JSX (not task.content), so use
    // the hand-authored narration scripts that mirror the on-screen
    // copy. Same 0.8 s settle delay as the regular path.
    if (currentNodeId === "task-1" || currentNodeId === "task-1a") {
      stopNarration();
      const script = currentNodeId === "task-1" ? TASK_1_NARRATION : TASK_1A_NARRATION;
      const tid = setTimeout(() => { narrateString(script); }, 800);
      return () => {
        clearTimeout(tid);
        stopNarration();
      };
    }
    if (!isTaskNarratable) return;
    // Stop any prior narration before starting the new one.
    stopNarration();
    // Wait 0.8 s after the new page renders before the voice begins
    // talking. Lets the layout settle and gives the user a beat to
    // visually register the page before audio kicks in.
    const tid = setTimeout(() => {
      playTaskNarration({ extraContent: buildExtraNarrationContent() });
    }, 800);
    return () => {
      clearTimeout(tid);
      stopNarration();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNodeId, narrationAuto, showNarrationModal, showIntro]);

  // Narrate the "How did that feel?" check-in modal when it opens.
  // We always play this (regardless of hands-free state) IF the user
  // has narration turned on — the modal is the most important
  // emotional moment of each task and benefits from a spoken nudge.
  const checkInNarrationFiredRef = useRef(false);
  useEffect(() => {
    if (!showCheckInModal) {
      checkInNarrationFiredRef.current = false;
      return;
    }
    if (checkInNarrationFiredRef.current) return;
    if (!narrationAuto && handsFreeRef.current === "off") return;
    checkInNarrationFiredRef.current = true;
    const modalCopy = [
      "How did that feel?",
      "You might notice tension in your body — heavy chest, tight muscles, a lump in your throat, tears. Or in your emotions — sadness, anger, worry, shame.",
      "Take a moment. Tap the scale below to check in with yourself.",
    ].join("\n\n");
    // Tiny delay so the modal's open animation lands before the voice.
    const tid = setTimeout(() => {
      narrateString(modalCopy);
    }, 400);
    return () => clearTimeout(tid);
  }, [showCheckInModal, narrationAuto, narrateString]);

  // Cleanup on unmount — never leave a clip playing after the user exits
  // the practice.
  useEffect(() => stopNarration, []);

  // Pre-warm the next task's narration in the background so when the
  // user actually navigates, the MP3s are already on disk and the
  // round-trip is ~200 ms instead of ~3 s. Fire-and-forget — we don't
  // care about the response, we just want the backend to generate
  // (and cache) the clips. Only runs if auto-narrate is on or hands-
  // free is running, so we don't waste TTS quota for users who never
  // turn narration on.
  useEffect(() => {
    if (!narrationAuto && handsFreeRef.current === "off") return;
    if (!node) return;
    // Predict the most likely "next" node — for a linear branch this
    // is exact; for distress-gated branches we just pick the "onLow"
    // path because that's what advances most users. Worst case the
    // wrong clip gets pre-warmed and the actual call still hits the
    // cache for whichever paragraphs overlap.
    let nextId = null;
    try { nextId = resolveNextNode(node, { sud: 0, answerYes: true }); } catch { /* ignore */ }
    if (!nextId || nextId === "task-1" || nextId === "task-1a") return;
    const nextNode = graph?.nodes?.[nextId];
    if (!nextNode?.title || !nextNode?.content) return;
    const id = setTimeout(() => {
      axios.post(`${API}/narration/task`, {
        title: nextNode.title,
        content: nextNode.content,
      }).catch(() => { /* fire-and-forget */ });
    }, 1500); // delay so we don't compete with the current task's narration fetch
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNodeId, narrationAuto]);

  // ---- Hands-free ("Play all") orchestrator ----------------------------
  // When the user enables hands-free on Task 1, we drive the whole
  // practice without further taps:
  //   1) narrate the task title + content
  //   2) if the task has a bilateral player → start it; on tone-complete
  //      either auto-advance, or speak the distress-slider prompt and
  //      stop (req 2: pause and wait for slider input)
  //   3) if the task has a text input → narrate the prompt, then stop
  //   4) otherwise → after a 1.5s breath, auto-advance to next task
  //
  // Any user gesture (slider change, text typing, menu click, etc.)
  // flips state to 'paused' and shows a "Resume hands-free" pill.
  const handsFreeStartedForRef = useRef(null); // last node we ran the chain for

  // Reset the per-node guard whenever we land on a new node so the chain
  // can run again on the new task.
  useEffect(() => {
    handsFreeStartedForRef.current = null;
  }, [currentNodeId]);

  const stopHandsFree = useCallback(() => {
    setHandsFreeState((prev) => {
      if (prev !== "off") {
        trackHandsFreeEvent({
          intervention_type: type,
          event_type: "stopped",
          task_index: currentTask,
          current_node_id: currentNodeId,
        });
      }
      return "off";
    });
    stopNarration();
    setNarrationState("idle");
    if (binauralPlayerRef.current?.isPlaying?.()) {
      binauralPlayerRef.current.stop();
    }
  }, [type, currentTask, currentNodeId]);

  const pauseHandsFree = useCallback(() => {
    // Only meaningful when actively running — ignore if already paused / off.
    setHandsFreeState((prev) => {
      if (prev === "off" || prev === "paused") return prev;
      trackHandsFreeEvent({
        intervention_type: type,
        event_type: "paused",
        task_index: currentTask,
        current_node_id: currentNodeId,
        reason: "user-gesture",
      });
      return "paused";
    });
    stopNarration();
    setNarrationState("idle");
    if (binauralPlayerRef.current?.isPlaying?.()) {
      binauralPlayerRef.current.stop();
    }
  }, [type, currentTask, currentNodeId]);

  const resumeHandsFree = useCallback(() => {
    handsFreeStartedForRef.current = null; // re-run the chain for the current task
    setHandsFreeState("running");
    trackHandsFreeEvent({
      intervention_type: type,
      event_type: "resumed",
      task_index: currentTask,
      current_node_id: currentNodeId,
    });
  }, [type, currentTask, currentNodeId]);

  // Per req 4 — any user interaction while hands-free is mid-flow
  // (actively narrating or playing the tone) pauses the experience.
  // Clicks that originate from inside the bilateral player or the
  // hands-free control pill itself are excluded so the user can still
  // adjust tone volume / stop hands-free without unintentionally
  // triggering this pause.
  useEffect(() => {
    if (handsFreeState !== "running") return undefined;
    const onClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest("[data-hands-free-safe]")) return;
      pauseHandsFree();
    };
    // Listen in the capture phase so we fire before downstream click
    // handlers (otherwise the button's onClick would race us).
    document.addEventListener("pointerdown", onClick, true);
    return () => document.removeEventListener("pointerdown", onClick, true);
  }, [handsFreeState, pauseHandsFree]);

  // When hands-free pauses for slider/text input, gently scroll the
  // distress slider into view so the user immediately knows what to do.
  useEffect(() => {
    if (handsFreeState !== "waiting-for-input") return;
    // Tiny delay so the prompt narration's last word lands before the
    // page scrolls — feels less jarring.
    const tid = setTimeout(() => {
      // Try the distress slider first, then the task-input textarea —
      // those are the two things the user might need to interact with
      // when hands-free pauses.
      const el = document.querySelector(".distress-slider, [data-testid='task-input']");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 350);
    return () => clearTimeout(tid);
  }, [handsFreeState]);

  // The orchestrator — fires once per (task, "running" state) transition.
  useEffect(() => {
    if (handsFreeState !== "running") return;
    if (handsFreeStartedForRef.current === currentNodeId) return;
    // Some special UI-only tasks (task-1a "How we'll know it's working")
    // carry no narratable title/content. They're meant to be read
    // visually, so we DON'T narrate AND we DON'T auto-skip them — we
    // pause hands-free here and let the user tap Continue when ready.
    // handleNextTask re-arms hands-free automatically (the
    // 'waiting-for-input' branch in handleNextTask) so narration picks
    // back up on the next narratable task.
    if (!task?.title || !task?.content) {
      handsFreeStartedForRef.current = currentNodeId;
      setHandsFreeState("waiting-for-input");
      return undefined;
    }
    handsFreeStartedForRef.current = currentNodeId;
    let cancelled = false;

    const advanceIfStillRunning = () => {
      if (cancelled) return;
      if (handsFreeRef.current !== "running") return;
      // 1.5 s breath between tasks so the experience feels paced, not
      // rushed.
      setTimeout(() => {
        if (cancelled || handsFreeRef.current !== "running") return;
        handleNextTaskRef.current?.();
      }, 1500);
    };

    // Wait 0.8 s after the page renders before narration begins —
    // gives the layout a beat to settle and the user a moment to
    // visually orient before the voice starts.
    const startTid = setTimeout(() => {
      if (cancelled || handsFreeRef.current !== "running") return;
      playTaskNarration({
      extraContent: buildExtraNarrationContent(),
      onEnd: (info) => {
        if (cancelled || handsFreeRef.current !== "running") return;
        if (info?.reason && info.reason !== "ended") return; // blocked/error → toast already shown
        // Decide what comes next based on this task's shape.
        if (task.hasPlayer && binauralPlayerRef.current) {
          // Brief pause so the narrator's last word doesn't bleed into
          // the tone's first beat.
          setTimeout(() => {
            if (cancelled || handsFreeRef.current !== "running") return;
            binauralPlayerRef.current.start();
          }, 600);
          // The tone's onComplete handler (wired below) takes over.
          return;
        }
        if (task.hasInput) {
          // User needs to type — stop hands-free here, let them work,
          // and continue manually with Continue.
          setHandsFreeState("waiting-for-input");
          return;
        }
        if (task.hasDistressSlider) {
          // Prompt the user to set the slider, then wait.
          const prompt = task.distressPrompt
            || "How distressed do you feel now, on a scale of zero to ten? Slide to your answer and tap Continue when ready.";
          narrateString(prompt, {
            onEnd: () => {
              if (cancelled || handsFreeRef.current !== "running") return;
              setHandsFreeState("waiting-for-input");
            },
          });
          return;
        }
        // No player, no input, no slider — auto-advance.
        advanceIfStillRunning();
      },
    });
    }, 800);

    return () => {
      cancelled = true;
      clearTimeout(startTid);
    };
    // handleNextTaskRef + narrate helpers are stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handsFreeState, currentNodeId, task?.hasPlayer, task?.hasInput, task?.hasDistressSlider]);
  // -----------------------------------------------------------------------
  // -----------------------------------------------------------------------

  // Build a chronological list of distress readings for the line graph.
  // Readings are stored keyed by `${nodeId}_after`. Walk the graph nodes in
  // task-number order so the chart reflects the natural session arc.
  const buildReadings = useCallback(() => {
    const levels = progress?.distress_levels || {};
    if (!graph?.nodes) return [];
    const entries = Object.entries(graph.nodes)
      .filter(([nodeId]) => {
        const key = `${nodeId}_after`;
        return levels[key] !== undefined && levels[key] !== null;
      })
      .map(([nodeId, n]) => ({
        nodeId,
        taskNumber: n.taskNumber || 0,
        distress: levels[`${nodeId}_after`],
      }))
      .sort((a, b) => a.taskNumber - b.taskNumber);
    return entries.map((e) => ({
      label: `T${e.taskNumber}`,
      distress: e.distress,
    }));
  }, [graph, progress]);

  // Auto-open the completion summary once the user lands on the final task.
  useEffect(() => {
    if (currentTask === 9 && !completionShownRef.current) {
      completionShownRef.current = true;
      // Capture the "completed hands-free practice" funnel event before
      // we tear down the orchestrator. This fires exactly once per
      // practice arrival on task-9, regardless of how the user got
      // there (manual taps or fully hands-free).
      if (handsFreeRef.current !== "off") {
        trackHandsFreeEvent({
          intervention_type: type,
          event_type: "completed",
          task_index: 9,
          current_node_id: currentNodeId,
        });
      }
      // Let the task-9 content render first, then softly reveal the overlay.
      const t = setTimeout(() => setShowCompletionSummary(true), 350);
      return () => clearTimeout(t);
    }
    // Reset the one-time flag when the user navigates away from task 9
    if (currentTask !== 9) {
      completionShownRef.current = false;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask]);

  useEffect(() => {
    // Wait for billing to come back before touching /progress/. Expired
    // and trial-locked users should land on the paywall view below —
    // hitting /progress/<type> for them returns 403/404 and was
    // navigating them back to /dashboard with a "Failed to load
    // intervention" toast (the "screen went fuzzy and froze" bug).
    if (!billingLoaded || isLocked) return;
    fetchOrCreateProgress();
  }, [type, billingLoaded, isLocked]);

  const loadDistressForNode = (progressData, nodeId) => {
    // Always reset the distress slider to null (unselected) when entering any node —
    // the user should actively set it each time rather than inheriting a stale value.
    setDistressAfter(null);
    setAnswerYes(null);
    setPlayCount(0);
    setShowNotImprovingSuggestion(false);
    setCelebratingSud(null);
  };

  // Fire a celebration — visual burst + inline pill + timeline glow.
  // `style` chooses between two looks:
  //   "side-confetti" — staggered confetti shells from random points along
  //                     the left and right edges, drifting toward center
  //   "fireworks"     — staggered upward shells from random horizontal origins
  const launchCelebration = useCallback((sud, style = "side-confetti") => {
    setCelebratingSud(sud);
    setMilestoneCount((c) => c + 1);

    // Gentle haptic cheer — native iOS gets Success notification, web falls back to vibrate
    hapticCelebrate();

    // Subtle celebration audio — fireworks burst sounds for the task-9 finale
    // (matches the visual), soft regal chime for the task 4-8 side-confetti
    // bursts. Silently skipped when the user has toggled celebrationSound off.
    // Both scale with the main volume slider.
    try {
      const prefs = getSoundPrefs();
      const audioVol = prefs.volume ?? 0.5;
      if (style === "fireworks") {
        playFireworksSoundtrack(audioVol);
      } else {
        playCelebrationChime(audioVol);
      }
    } catch (e) {
      // Visual celebration still fires; audio failure is non-fatal but trace it.
      logger.warn("celebration audio failed:", e);
    }

    const colors = ["#3090A8", "#78C0D8", "#E2B887", "#AFC7F0", "#F5D4A8", "#FFFFFF"];

    if (style === "fireworks") {
      // Overlapping firework shells — bursts detonate close together in time
      // and position, creating the "multiple fireworks exploding around each
      // other" feel. Each shell has varied size, color palette, and origin,
      // so no two bursts look identical. Total show ~2.4s.
      const shellPalettes = [
        ["#F5D4A8", "#E2B887", "#FFFFFF"],         // warm gold
        ["#AFC7F0", "#78C0D8", "#FFFFFF"],         // cool blue
        ["#3090A8", "#B9D4AE", "#FFFFFF"],         // soft green
        ["#E2B887", "#AFC7F0", "#F5D4A8", "#FFFFFF"], // mixed
      ];
      const shellCount = sud === 0 ? 10 : 7;
      for (let i = 0; i < shellCount; i += 1) {
        // Irregular timing: mostly quick but occasionally overlapping closely.
        const delay = i * 140 + secureRandom() * 120;
        const palette = shellPalettes[i % shellPalettes.length];
        // Varied burst "sizes" — some small/bright, some larger/wider
        const big = secureRandom() > 0.55;
        const particleCount = big ? 90 : 55;
        const spread = big ? 360 : 260;
        const startVelocity = big ? 55 : 42;
        const origin = {
          x: 0.1 + secureRandom() * 0.8,
          // Burst up high enough to look like they're in the sky
          y: 0.25 + secureRandom() * 0.4,
        };
        setTimeout(() => {
          confetti({
            particleCount,
            spread,
            startVelocity,
            ticks: 220,
            gravity: 0.85,
            zIndex: 9999,
            colors: palette,
            shapes: ["circle"],
            origin,
            scalar: big ? 1.0 : 0.85,
          });
          // Immediate second smaller "echo" burst from nearly the same spot,
          // a fraction of a beat later — gives each shell a "re-bloom" feel.
          if (secureRandom() > 0.35) {
            setTimeout(() => {
              confetti({
                particleCount: 28,
                spread: 360,
                startVelocity: 28,
                ticks: 180,
                gravity: 0.95,
                zIndex: 9999,
                colors: palette,
                shapes: ["circle"],
                origin: {
                  x: Math.max(0, Math.min(1, origin.x + (secureRandom() - 0.5) * 0.06)),
                  y: Math.max(0, Math.min(1, origin.y + (secureRandom() - 0.5) * 0.04)),
                },
                scalar: 0.7,
              });
            }, 90 + secureRandom() * 80);
          }
        }, delay);
      }
    } else {
      // Side-confetti — staggered bursts from random points along the left
      // and right edges, angled toward the center so the streamers sweep
      // inward across the screen.
      const sideBase = { startVelocity: 50, spread: 70, ticks: 200, zIndex: 9999, colors };
      const burstCount = sud === 0 ? 6 : 4;
      for (let i = 0; i < burstCount; i += 1) {
        const fromLeft = i % 2 === 0;
        setTimeout(() => {
          confetti({
            ...sideBase,
            particleCount: 80,
            angle: fromLeft ? 60 + secureRandom() * 20 : 100 + secureRandom() * 20,
            origin: {
              x: fromLeft ? secureRandom() * 0.05 : 0.95 + secureRandom() * 0.05,
              y: 0.3 + secureRandom() * 0.5,
            },
          });
        }, i * 180);
      }
    }

    // Auto-hide the inline pill after a few seconds
    setTimeout(() => setCelebratingSud(null), 4500);
  }, []);

  // Watch distress changes and fire celebrations once per node visit.
  // (No longer triggers on value change — now fires only on advance via handleNextTask.)

  // Wipe the per-node celebration record when a new intervention flow begins.
  useEffect(() => {
    celebratedNodesRef.current = new Set();
    celebratedTasksRef.current = new Set();
    setMilestoneCount(0);
    // Reset visit tallies + metaphor history when a new intervention starts
    visitCountsRef.current = {};
    setCurrentMetaphor(null);
    resetMetaphorHistory();
    resetEncouragementHistory();
    // First bilateral cue of the new intervention should be the signature opener.
    resetBreathCues();
  }, [type]);

  // Track visits to loop-back nodes and surface a fresh metaphor on every
  // *other* pass so the imagery stays rare enough to feel special rather than
  // expected. Visit 1/3/5 shows a metaphor; 2/4/6 skip it.
  // Depends on navSeq so self-loop navigations (A1 → A1) also refresh the metaphor.
  useEffect(() => {
    if (!currentNodeId) return;
    const counts = visitCountsRef.current;
    counts[currentNodeId] = (counts[currentNodeId] || 0) + 1;
    const visit = counts[currentNodeId];
    if (METAPHOR_NODES.includes(currentNodeId) && visit % 2 === 1) {
      setCurrentMetaphor(pickMetaphor());
    } else {
      setCurrentMetaphor(null);
    }
    // METAPHOR_NODES is a stable constant; safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNodeId, navSeq]);

  // Scroll to the top of the page after every navigation (Continue or Previous),
  // including self-loops. Runs in an effect so the scroll happens *after* React
  // commits the new task content — reliable across browsers.
  useEffect(() => {
    if (navSeq === 0) return; // don't scroll on initial mount
    // Instant scroll on the first render beat, then allow user-initiated smooth
    // scrolling afterwards. Using `behavior: 'instant'` avoids a visible
    // half-animation when the new page is shorter than the previous one.
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [navSeq]);

  // Show the narration opt-in modal the first time the user starts this
  // specific practice — but only AFTER the "How this helps" intro card
  // has been dismissed, so the user can still back out and pick a
  // different practice without committing to a narration choice yet.
  useEffect(() => {
    if (loading) return;
    if (currentNodeId !== "task-1") return;
    if (showIntro) return;
    if (hasNarrationChoiceFor(type)) return;
    setShowNarrationModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, currentNodeId, type, showIntro]);

  const handleNarrationModalChoice = (choice) => {
    // CRITICAL: unlock the audio element synchronously inside this click
    // gesture. Otherwise the browser will reject the FIRST play() after a
    // network fetch (TTS cold start) because the gesture window has closed.
    unlockAudio();
    saveNarrationChoiceFor(type, choice);
    const wantNarration = choice === "on";
    setNarrationPref(wantNarration);
    setNarrationAuto(wantNarration);
    setShowNarrationModal(false);
  };

  const fetchOrCreateProgress = async () => {
    // Guest mode: hydrate from localStorage (or seed fresh) and skip the
    // authenticated /api/progress calls entirely.
    if (isGuest) {
      const stored = readGuestSession();
      const seed = stored || writeGuestSession({
        practice_type: GUEST_PRACTICE_TYPE,
        current_node_id: "task-1",
        last_task_number: 0,
      });
      setProgress({
        type,
        current_node_id: seed?.current_node_id || "task-1",
        task_responses: seed?.task_responses || {},
        distress_levels: seed?.distress_levels || {},
      });
      const nodeId = seed?.current_node_id || "task-1";
      setCurrentNodeId(nodeId);
      setTaskInput(seed?.task_responses?.[nodeId] || "");
      trackGuestStart();
      setLoading(false);
      // Show the "Is this practice right for you?" intro on first landing.
      try {
        const seen = localStorage.getItem(introStorageKey);
        if (!seen) setShowIntro(true);
      } catch (_) { /* private-mode safe */ }
      return;
    }
    try {
      const response = await axios.get(`${API}/progress/${type}`, { withCredentials: true });
      setProgress(response.data);
      const nodeId = response.data.current_node || "task-1";
      setCurrentNodeId(nodeId);
      setTaskInput(response.data.task_responses?.[nodeId] || "");
      loadDistressForNode(response.data, nodeId);
    } catch (error) {
      if (error.response?.status === 404) {
        try {
          const createResponse = await axios.post(
            `${API}/progress`,
            { intervention_type: type },
            { withCredentials: true }
          );
          setProgress(createResponse.data);
          setCurrentNodeId("task-1");
        } catch (createError) {
          logger.error("Failed to create progress:", createError);
          toast.error("Failed to start intervention");
          navigate('/dashboard');
        }
      } else {
        logger.error("Failed to fetch progress:", error);
        toast.error("Failed to load intervention");
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
      // Open the "Is this practice right for you?" intro card when the user
      // lands on task-1 for the first time. Skipping when they've already
      // dismissed it (per-intervention key) keeps the flow snappy on
      // repeat visits.
      try {
        const seen = localStorage.getItem(introStorageKey);
        if (!seen) setShowIntro(true);
      } catch (_) {
        // localStorage may throw in private-mode iOS — fall through silently.
      }
    }
  };

  const saveProgress = async (updates) => {
    // Guest mode: no auth token, so /api/progress would 401. Persist
    // task_responses + distress_levels + current_node_id to
    // localStorage and short-circuit. The user's next signup will
    // migrate this to the server via /api/guest/migrate.
    if (isGuest) {
      setSaving(true);
      try {
        const nextState = writeGuestSession({
          practice_type: GUEST_PRACTICE_TYPE,
          current_node_id: updates.current_node_id ?? currentNodeId,
          task_responses: {
            ...(readGuestSession()?.task_responses || {}),
            ...(updates.task_responses || {}),
          },
          distress_levels: {
            ...(readGuestSession()?.distress_levels || {}),
            ...(updates.distress_levels || {}),
          },
        });
        // Mirror the local state shape the rest of the component expects.
        setProgress((prev) => ({
          ...(prev || {}),
          type,
          ...updates,
          current_node_id: nextState?.current_node_id || updates.current_node_id,
        }));
        return nextState;
      } finally {
        setSaving(false);
      }
    }
    // Retry once on transient failure — iOS cellular / WebView networks
    // occasionally drop the first PUT of a session (DNS warm-up, cold
    // connection). One quiet retry after a short pause is enough to
    // absorb that without the user ever seeing a scary toast. Only 5xx
    // or network errors are retried; a 4xx surfaces immediately so we
    // don't hammer the server on a truly bad request.
    setSaving(true);
    const attempt = async () =>
      axios.put(`${API}/progress/${type}`, updates);
    try {
      let response;
      try {
        response = await attempt();
      } catch (firstErr) {
        const status = firstErr?.response?.status;
        const isTransient = !status || status >= 500;
        if (!isTransient) throw firstErr;
        await new Promise((r) => setTimeout(r, 1200));
        response = await attempt();
      }
      setProgress(response.data);
      return response.data;
    } catch (error) {
      logger.error("Failed to save progress:", error);
      // Friendlier, actionable copy — tells the user WHAT to do instead
      // of the raw "Failed to save progress" (which sounded like data
      // loss).
      toast.error("Couldn't save your progress. Check your connection and tap Continue again.");
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const gatherDistressUpdates = () => {
    if (distressAfter === null) return undefined;
    return { [`${currentNodeId}_after`]: distressAfter };
  };

  const handlePauseSession = async () => {
    try {
      await saveProgress({
        is_paused: true,
        task_responses: taskInput ? { [currentNodeId]: taskInput } : undefined,
        distress_levels: gatherDistressUpdates()
      });
      toast.success("Session paused. You can continue later.");
      navigate('/dashboard');
    } catch (error) {
      logger.error("Pause session failed:", error);
    }
  };

  const handleNextTask = async () => {
    if (!node) return;
    // Guest mode: hard-block once the user has completed GUEST_MAX_TASK.
    // We check the CURRENT task-number the user just finished, not the
    // node we're about to navigate to, so the modal fires immediately
    // after Continue on Task 4 with no glimpse of Task 5's content.
    if (isGuest) {
      const currentTaskNum = node?.taskNumber || 0;
      if (currentTaskNum >= GUEST_MAX_TASK) {
        // Persist the last state so the migration hook can restore it.
        writeGuestSession({
          current_node_id: currentNodeId,
          last_task_number: currentTaskNum,
          task_responses: {
            ...(readGuestSession()?.task_responses || {}),
            ...(taskInput ? { [currentNodeId]: taskInput } : {}),
          },
        });
        trackGuestTaskCompleted(currentTaskNum);
        setShowGuestUpgrade(true);
        return;
      }
      // Guest still within the free window — record the completion for
      // analytics before the normal advance logic runs.
      if (currentTaskNum > 0) {
        trackGuestTaskCompleted(currentTaskNum);
        writeGuestSession({ last_task_number: currentTaskNum });
      }
    }
    // CRITICAL: pre-unlock the audio element inside this click gesture so
    // the auto-narrate effect on the next task can call audio.play() even
    // after the TTS fetch (which takes time) has consumed the gesture window.
    unlockAudio();
    // Mark this navigation as forward so the destination task's
    // auto-narrate effect plays (per req 3).
    lastNavDirectionRef.current = "forward";
    // Stop narration immediately on navigation so the previous task's
    // voice doesn't continue speaking on the new screen. Bump the seq
    // so any in-flight fetch is invalidated when it eventually
    // resolves.
    narrationSeqRef.current += 1;
    stopNarration();
    setNarrationState("idle");
    const distressData = gatherDistressUpdates();
    const nextNodeId = resolveNextNode(node, { sud: distressAfter, answerYes });

    // Decide whether this advance qualifies for a celebration. We don't fire
    // the confetti here — it gets queued and only fires AFTER the user has
    // actually moved to the next task (see end of this function). We don't fire
    // the confetti here — it gets queued and only fires AFTER the user has
    // actually moved to the next task (see end of this function).
    //
    // Celebration fires when the user *arrives* at any new task from 4
    // through 9. Side-branch loops within a task (e.g. A1 at task 3, H/I at
    // task 6) do NOT count as advances and do NOT consume the once-per-task
    // celebration slot. Style: tasks 4-8 use side-confetti (random side
    // origins sweeping toward center); task 9 (the finale) uses fireworks.
    const sudQualifies = distressAfter === 0 || distressAfter === 1;
    const milestoneTargets = new Set([
      "task-4", "task-5", "task-6", "task-7", "task-8", "task-9",
    ]);
    const isMilestoneAdvance = milestoneTargets.has(nextNodeId);
    const shouldCelebrate =
      sudQualifies
      && isMilestoneAdvance
      && !celebratedTasksRef.current.has(nextNodeId);
    const celebrateSud = distressAfter;
    const celebrateKey = nextNodeId;
    const celebrateStyle = nextNodeId === "task-9" ? "fireworks" : "side-confetti";

    if (nextNodeId === null) {
      // End of flow — mark intervention completed
      try {
        await saveProgress({
          is_completed: true,
          task_responses: taskInput ? { [currentNodeId]: taskInput } : undefined,
          distress_levels: distressData
        });
        // Fire the streak-day event. Fire-and-forget: if it fails we
        // don't want to hold up the completion celebration. Milestone
        // detection (if any) surfaces via the Dashboard on next visit.
        postStreakEvent({ source: "practice", refId: type }).catch(() => {});
        toast.success("Congratulations! You've completed this intervention.");
        navigate('/dashboard');
      } catch (error) {
        logger.error("Complete intervention failed:", error);
      }
      return;
    }

    try {
      const nextNode = graph.nodes[nextNodeId];
      const updated = await saveProgress({
        current_node: nextNodeId,
        current_task: nextNode?.taskNumber || 1,
        task_responses: taskInput ? { [currentNodeId]: taskInput } : undefined,
        distress_levels: distressData
      });
      setNodeHistory((h) => [...h, currentNodeId]);
      setCurrentNodeId(nextNodeId);
      setNavSeq((n) => n + 1);
      setShowCheckInModal(false);
      // If hands-free was waiting for the user to provide the slider /
      // input on the previous task, automatically re-arm it now so the
      // narration + tone chain continues on the new task without an
      // extra "Resume" tap.
      if (handsFreeRef.current === "waiting-for-input") {
        handsFreeStartedForRef.current = null;
        setHandsFreeState("running");
      }
      setTaskInput(updated?.task_responses?.[nextNodeId] || "");
      loadDistressForNode(updated, nextNodeId);

      // Fire the celebration only after the next task has actually become
      // the current node. Keyed per-milestone so each task arrival (4-9)
      // gets its own one-shot burst, with the style alternating between
      // confetti and fireworks.
      if (shouldCelebrate) {
        celebratedTasksRef.current.add(celebrateKey);
        celebratedNodesRef.current.add(currentNodeId);
        // Defer one frame so the new task DOM is in place before the
        // celebration renders over it.
        setTimeout(() => launchCelebration(celebrateSud, celebrateStyle), 60);
      }
    } catch (error) {
      logger.error("Next task failed:", error);
    }
  };

  // Keep a stable ref to handleNextTask so the hands-free orchestrator
  // can call the latest version without resubscribing the effect.
  const handleNextTaskRef = useRef(handleNextTask);
  useEffect(() => { handleNextTaskRef.current = handleNextTask; });

  const handlePreviousTask = async () => {
    if (!node) return;
    // Flag this navigation as backward so the auto-narrate effect on
    // the destination task stays silent (per req 4).
    lastNavDirectionRef.current = "backward";
    // Stop any in-flight narration immediately so the old task's
    // voice doesn't keep talking after the user navigated away. Bump
    // the seq so any in-flight fetch is invalidated when it resolves.
    narrationSeqRef.current += 1;
    stopNarration();
    setNarrationState("idle");
    const distressData = gatherDistressUpdates();

    // Priority 1 — walk the in-session breadcrumb stack (handles all branch paths)
    if (nodeHistory.length > 0) {
      const prevNodeId = nodeHistory[nodeHistory.length - 1];
      const prevNode = graph.nodes[prevNodeId];
      if (!prevNode) return;
      try {
        const updated = await saveProgress({
          current_node: prevNodeId,
          current_task: prevNode.taskNumber || 1,
          task_responses: taskInput ? { [currentNodeId]: taskInput } : undefined,
          distress_levels: distressData
        });
        setNodeHistory((h) => h.slice(0, -1));
        setCurrentNodeId(prevNodeId);
        setNavSeq((n) => n + 1);
        setTaskInput(updated?.task_responses?.[prevNodeId] || "");
        loadDistressForNode(updated, prevNodeId);
      } catch (error) {
        logger.error("Previous task failed:", error);
      }
      return;
    }

    // Priority 2 — no history (e.g. a paused session resumed mid-flow).
    // Pick a sensible fallback: branch pages snap back to their parent main task,
    // main tasks step back one task number.
    let prevNodeId = null;
    if (/^task-\d+$/.test(currentNodeId)) {
      if (node.taskNumber <= 1) return;
      prevNodeId = `task-${node.taskNumber - 1}`;
    } else {
      prevNodeId = `task-${node.taskNumber}`;
    }
    if (!prevNodeId || !graph.nodes[prevNodeId]) return;

    try {
      const prevNode = graph.nodes[prevNodeId];
      const updated = await saveProgress({
        current_node: prevNodeId,
        current_task: prevNode.taskNumber || 1,
        task_responses: taskInput ? { [currentNodeId]: taskInput } : undefined,
        distress_levels: distressData
      });
      setCurrentNodeId(prevNodeId);
      setNavSeq((n) => n + 1);
      setTaskInput(updated?.task_responses?.[prevNodeId] || "");
      loadDistressForNode(updated, prevNodeId);
    } catch (error) {
      logger.error("Previous task failed:", error);
    }
  };

  // Loading spinner — but only for users who CAN access the practice.
  // For locked users we render the paywall block below regardless of
  // `loading` (which never flips to false because we skip the
  // /progress/ fetch for them).
  if (loading && !(billingLoaded && isLocked)) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3090A8]"></div>
      </div>
    );
  }

  // Trial paywall — for users on the 5-day free trial whose plan hasn't
  // unlocked this specific practice, AND for expired users (every
  // practice is locked once the trial ends). Rendered as soon as billing
  // status comes back so we don't paint a flash of the flow first OR
  // try to GET /progress/<type> for a user who can't access it.
  if (billingLoaded && isLocked) {
    const catalogEntry = getPracticeBySlug(type) || {};
    const title = catalogEntry.title || "This practice";
    const hook = catalogEntry.hook || "";
    const image = catalogEntry.image || "/images/interventions/quiet-the-worry.webp";
    const expired = isTrialExpired(billingStatus);
    return (
      <div className="min-h-screen bg-[#FBF8F1]" data-testid={`intervention-locked-${type}`}>
        <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
          <div className="px-6 sm:px-10 py-5 max-w-5xl mx-auto flex items-center justify-between">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              data-testid="intervention-locked-back-btn"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-[#1B2349]" />
            </button>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 pt-6 pb-20 text-center">
          <div
            className="aspect-square w-44 mx-auto rounded-3xl bg-cover bg-center mb-7 ring-1 ring-white/40 shadow-[0_10px_40px_rgba(27,35,73,0.18)]"
            style={{ backgroundImage: `url('${image}')` }}
            aria-hidden
          />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E2B887]/15 text-[#7A5C2E] text-xs font-medium mb-4">
            <Lock className="w-3.5 h-3.5" />
            {expired ? "Trial ended — subscribe to continue" : "Unlock with a paid plan"}
          </div>
          <h1 className="font-outfit font-semibold text-3xl sm:text-4xl text-[#1B2349] mb-3 leading-tight">
            {title}
          </h1>
          {hook && (
            <p className="text-[#4A5568] text-base sm:text-lg leading-relaxed mb-6 max-w-lg mx-auto">
              {hook}
            </p>
          )}
          <p className="text-sm text-[#718096] mb-7 max-w-md mx-auto">
            {expired
              ? "Your free trial has ended. Subscribe to keep using every guided practice and the Daily Practices audio library."
              : <>During your free trial you can run <strong>Quiet The Worry</strong> and <strong>Dial Down Body Tension</strong>. Upgrade any time to unlock every guided practice.</>}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              onClick={() => navigate("/pricing")}
              className="btn-primary text-base px-7 py-3"
              data-testid="intervention-locked-upgrade-btn"
            >
              {expired ? "Subscribe now" : "See plans & upgrade"}
            </Button>
            {!expired && (
              <Button
                variant="outline"
                onClick={() => navigate("/intervention/quiet_the_worry")}
                className="rounded-full border-slate-200"
                data-testid="intervention-locked-try-free-btn"
              >
                Try Quiet The Worry
              </Button>
            )}
          </div>
        </main>
      </div>
    );
  }

  // No graph for this slug — render a friendly "not found" instead of an
  // infinite spinner. This catches old/external links to renamed practices
  // (e.g. /intervention/anxiety, which now lives at /intervention/quiet_the_worry).
  if (!graph) {
    return (
      <div
        className="min-h-screen bg-[#FBF8F1] flex items-center justify-center px-6"
        data-testid="intervention-not-found"
      >
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] p-10 max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-[#FEF3E7] flex items-center justify-center mx-auto mb-5">
            <span className="text-2xl">·</span>
          </div>
          <h1 className="font-outfit font-semibold text-2xl text-[#1B2349] mb-2">
            Practice not found
          </h1>
          <p className="text-[#4A5568] mb-7 leading-relaxed">
            The link you followed doesn't match any of our current practices.
            They may have been renamed — head back to your dashboard to find the right one.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-6 py-3 rounded-full bg-[#1B2349] text-white font-medium hover:bg-[#0F1838] transition-colors"
            data-testid="intervention-not-found-back-btn"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  // Graph exists but the entry node hasn't materialised yet (race during
  // initial fetch of progress). Brief spinner is fine here.
  if (!node) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3090A8]"></div>
      </div>
    );
  }

  const progressPercent = ((currentTask - 1) / 9) * 100;

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="intervention-flow">
      <NarrationChoiceModal
        open={showNarrationModal}
        practiceTitle={INTERVENTION_TITLES[type] || "this practice"}
        onChoose={handleNarrationModalChoice}
      />
      {showGuestUpgrade && (
        <GuestUpgradeModal
          onSignUp={() => {
            // Route to signup; after successful auth, AuthCallback will
            // read `post_auth_redirect` and bring the user back to the
            // practice. `migrateGuestSessionIfPresent` (mounted in App.js)
            // fires as soon as `user` is set, so their task 1-4 responses
            // land in the server-side progress doc automatically.
            try {
              sessionStorage.setItem(
                "post_auth_redirect",
                `/intervention/${GUEST_PRACTICE_TYPE}`
              );
            } catch (_) { /* ignore */ }
            navigate("/signup");
          }}
          onDismiss={() => {
            const taskNum = node?.taskNumber || 0;
            trackGuestAbandonedAtTask(taskNum);
            setShowGuestUpgrade(false);
            navigate("/");
          }}
        />
      )}
      {pendingCelebrationTask !== null && (
        <StepCompletionCelebration
          currentTaskNumber={pendingCelebrationTask}
          nextTaskTitle={
            // Resolve the destination task's title so the "Next up" line is
            // specific. Uses the same resolveNextNode helper as handleNextTask
            // to guarantee the title matches whatever branch we'd actually
            // navigate to.
            (() => {
              try {
                const nextId = resolveNextNode(node, {
                  sud: distressAfter,
                  answerYes,
                });
                return graph?.nodes?.[nextId]?.title;
              } catch { return undefined; }
            })()
          }
          totalTasks={9}
          isGuest={isGuest}
          onContinue={() => {
            setPendingCelebrationTask(null);
            handleNextTask();
          }}
          onPause={() => {
            setPendingCelebrationTask(null);
            navigate(isGuest ? "/" : "/dashboard");
          }}
        />
      )}
      {/* Header */}
      <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-[#1B2349]" />
              </button>
              <div>
                <p className="text-[#718096] text-sm">Practice</p>
                <h1 className="font-outfit font-medium text-[#1B2349]">
                  {INTERVENTION_TITLES[type] || type}
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {currentTask === 9 && (
                <Button
                  onClick={() => setShowCompletionSummary(true)}
                  variant="outline"
                  className="border-[#78C0D8] text-[#78C0D8] hover:bg-[#78C0D8] hover:text-white rounded-full"
                  data-testid="review-summary-btn"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Review Summary
                </Button>
              )}
              <Button
                onClick={handlePauseSession}
                variant="outline"
                className="border-[#3090A8] text-[#3090A8] hover:bg-[#3090A8] hover:text-white rounded-full"
                disabled={saving}
                data-testid="pause-session-btn"
              >
                <Pause className="w-4 h-4 mr-2" />
                Pause Session
              </Button>
              <AppMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between text-sm text-[#718096] mb-2">
            <span>Task {currentTask} of 9</span>
            <span>{Math.round(progressPercent)}% complete</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
          
          {/* Step indicators */}
          <div className="flex items-center justify-between mt-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((step) => (
              <div
                key={step}
                className={`progress-step ${
                  step < currentTask ? 'completed' :
                  step === currentTask ? 'current' : 'upcoming'
                } ${step === currentTask && celebratingSud !== null ? 'step-celebrating' : ''}`}
              >
                {step < currentTask ? <Check className="w-4 h-4" /> : step}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 pb-32">
        {currentNodeId === "task-1" ? (
          <GettingStartedTaskContent
            narrationOn={narrationAuto}
            onChangeNarrationInSettings={() => navigate('/settings')}
          />
        ) : currentNodeId === "task-1a" ? (
          <GoalAndCheckInContent />
        ) : currentTask === 9 ? (
          <div className="space-y-6">
            <Task9Content task={task} />
            {/* Reflection input + final slider still render inside their own card below */}
            <div className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 animate-fade-in-up overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#3090A8] via-[#78C0D8] to-[#E2B887] rounded-t-2xl" />
              {task.hasDistressSlider && (
                <div className="mb-6">
                  <DistressChecker
                    label={task.distressPrompt || "How distressed do you feel now?"}
                    value={distressAfter}
                    onChange={setDistressAfter}
                    testId="distress-after"
                  />
                </div>
              )}
              {task.hasInput && (
                <div>
                  <label className="block text-[#1B2349] font-medium mb-2">
                    {task.inputPrompt}
                  </label>
                  <Textarea
                    value={taskInput}
                    onChange={(e) => setTaskInput(e.target.value)}
                    placeholder="Take your time to reflect and write..."
                    className="min-h-[150px] rounded-xl border-slate-200 focus:border-[#3090A8] focus:ring-[#3090A8]/30"
                    data-testid="task-input"
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 animate-fade-in-up overflow-hidden">
            {/* Accent gradient strip at the top of the card */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#3090A8] via-[#78C0D8] to-[#E2B887] rounded-t-2xl" />

            <div className="flex items-start gap-3 mb-6 pt-2">
              <h2 className="font-outfit font-semibold text-[1.375rem] sm:text-2xl text-[#1B2349] leading-tight flex-1">
                {task.title}
              </h2>
              {/* Narration speaker — reads the task title + content aloud
                  in a calm voice. While narrating, the icon morphs into a
                  Stop square so the user always knows the action will do. */}
              <button
                type="button"
                onClick={() => {
                  // Pre-unlock the audio element inside this click gesture
                  // — same reason as in handleNextTask. The TTS fetch that
                  // follows can consume the gesture window otherwise.
                  unlockAudio();
                  if (narrationState === "playing") {
                    stopTaskNarration();
                  } else if (narrationState === "idle") {
                    playTaskNarration({ extraContent: buildExtraNarrationContent() });
                  }
                }}
                disabled={narrationState === "loading"}
                aria-label={
                  narrationState === "playing"
                    ? "Stop narration"
                    : narrationState === "loading"
                    ? "Loading narration"
                    : "Read this aloud"
                }
                title={
                  narrationState === "playing"
                    ? "Stop narration"
                    : "Read this aloud"
                }
                className={`flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                  narrationState === "playing"
                    ? "bg-[#3090A8] text-white hover:bg-[#246F82]"
                    : "bg-[#EAF0F9] text-[#3090A8] hover:bg-[#D6E2F2]"
                } disabled:opacity-50 disabled:cursor-wait`}
                data-testid="narrate-task-btn"
              >
                {narrationState === "loading" ? (
                  <span
                    className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
                    aria-hidden="true"
                  />
                ) : narrationState === "playing" ? (
                  <Square className="w-4 h-4" fill="currentColor" />
                ) : (
                  <Speaker className="w-5 h-5" />
                )}
              </button>
            </div>

            <div className="task-content text-[#1B2349] leading-relaxed mb-8">
              {task.celebration && task.content?.includes("{{CELEBRATION}}") ? (
                (() => {
                  const [rawBefore, rawAfter] = task.content.split("{{CELEBRATION}}");
                  const before = rawBefore.replace(/\n{1,50}$/, "");
                  const after = rawAfter.replace(/^\n{1,50}/, "");
                  return (
                    <div className="whitespace-pre-line">
                      {renderInlineBold(before)}
                      <span
                        className="block font-bold italic text-[#3090A8] my-4"
                        data-testid="celebration-line"
                      >
                        {task.celebration}
                      </span>
                      {renderInlineBold(after)}
                    </div>
                  );
                })()
              ) : (
                // Split content into paragraphs so each gets proper breathing
                // room. The final paragraph is always the "start the bilateral
                // sound and follow along" instruction — lift it into a subtle
                // sky-blue callout so there's a clear visual beat between
                // reflective reading and actionable direction.
                (() => {
                  const paragraphs = (task.content || "")
                    .split(/\n\s*\n/)
                    .map((p) => p.trim())
                    .filter(Boolean);
                  if (paragraphs.length === 0) return null;
                  const lastIdx = paragraphs.length - 1;
                  const looksLikeAction = paragraphs.length > 1
                    && /\b(start the bilateral|bilateral sound|let the bilateral|play the bilateral|let your mind|keep moving your eyes|keep tapping|keep your eyes moving|move your eyes|tap your (legs|palms|knees))\b/i
                      .test(paragraphs[lastIdx]);
                  return paragraphs.map((p, i) => {
                    const isActionBeat = looksLikeAction && i === lastIdx;
                    if (isActionBeat) {
                      return (
                        <div
                          key={i}
                          className="mt-6 rounded-xl bg-[#EAF0F9]/60 border border-[#D4E0F1] px-5 py-4 flex gap-3 items-start"
                          data-testid={`task-action-beat`}
                        >
                          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-[0_2px_6px_rgba(130,160,216,0.18)]">
                            <Play className="w-4 h-4 text-[#78C0D8]" />
                          </div>
                          <p className="text-[#1B2349] leading-relaxed whitespace-pre-line">
                            {renderInlineBold(p)}
                          </p>
                        </div>
                      );
                    }
                    return (
                      <p
                        key={i}
                        className="text-[#1B2349] leading-relaxed whitespace-pre-line mb-5 last:mb-0"
                      >
                        {renderInlineBold(p)}
                      </p>
                    );
                  });
                })()
              )}
            </div>

          {/* Metaphor imagery — surfaced every other loop-back pass on the
              branch nodes. Plays a one-shot pulse animation on mount so it
              catches the eye without being loud. `key` forces re-animation
              whenever the metaphor text changes. */}
          {currentMetaphor && (
            <div
              key={currentMetaphor}
              className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-[#EEF4EE] via-[#F3F7F2] to-[#EAF0F9] border border-slate-100 animate-metaphor-pulse"
              data-testid="metaphor-card"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 w-8 h-8 rounded-full bg-white/70 flex items-center justify-center flex-shrink-0 shadow-[0_2px_8px_rgba(139,168,136,0.18)]">
                  <Sparkles className="w-4 h-4 text-[#3090A8]" />
                </div>
                <p className="text-[#1B2349] italic leading-relaxed" data-testid="metaphor-text">
                  {currentMetaphor}
                </p>
              </div>
            </div>
          )}

          {/* Binaural Player for any node that includes bilateral stimulation */}
          {task.hasPlayer && (
            <div className="mb-6" data-hands-free-safe>
              <BinauralPlayer ref={binauralPlayerRef} onComplete={() => {
                const newCount = playCount + 1;
                setPlayCount(newCount);
                if (currentTask === 4 && newCount >= 3 && distressAfter !== null && distressAfter > 5) {
                  setShowNotImprovingSuggestion(true);
                }
                // When the player wraps, open the floating forced-choice modal
                // if this node expects a distress check-in or Yes/No answer.
                // Small delay lets the breath cue land before the modal slides in.
                const needsCheckIn = task.hasDistressSlider
                  || task.branches?.kind === "distress-question-gate"
                  || task.branches?.kind === "distress-yesno-gate";
                if (needsCheckIn) {
                  setTimeout(() => setShowCheckInModal(true), 900);
                }
                // Hands-free continuation: tone has ended, decide what
                // comes next. If a check-in / slider / input is needed,
                // narrate the prompt and wait. Otherwise auto-advance.
                if (handsFreeRef.current === "running") {
                  if (needsCheckIn || task.hasInput) {
                    const prompt = task.distressPrompt
                      || "How distressed do you feel now, on a scale of zero to ten? Slide to your answer and tap Continue when ready.";
                    setTimeout(() => {
                      if (handsFreeRef.current !== "running") return;
                      narrateString(prompt, {
                        onEnd: () => {
                          if (handsFreeRef.current !== "running") return;
                          setHandsFreeState("waiting-for-input");
                        },
                      });
                    }, 900);
                  } else {
                    setTimeout(() => {
                      if (handsFreeRef.current !== "running") return;
                      handleNextTaskRef.current?.();
                    }, 1500);
                  }
                }
              }} />
            </div>
          )}

          {/* Auto-suggest: Not Improving banner after 3+ plays with distress > 5 on the first bilateral task */}
          {showNotImprovingSuggestion && currentTask === 4 && (
            <div
              className="mb-6 p-4 rounded-xl bg-[#FEF3E7] border border-[#E2B887]/30 animate-fade-in-up"
              data-testid="not-improving-suggestion"
            >
              <div className="flex items-start gap-3">
                <HelpCircle className="w-5 h-5 text-[#B8965A] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#96773A] mb-1">
                    Feeling stuck?
                  </p>
                  <p className="text-sm text-[#B8965A] mb-3">
                    You've done {playCount} rounds and your distress is still above 5. There might be a deeper root behind this — and that's completely normal.
                  </p>
                  <Button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/not-improving`, {
                          intervention_type: type,
                          distress_after: distressAfter
                        }, { withCredentials: true });
                      } catch (error) {
                        logger.error("Failed to track not-improving:", error);
                      }
                      navigate(`/intervention/${type}/not-improving`);
                    }}
                    size="sm"
                    className="bg-[#E2B887] hover:bg-[#D4A06A] text-white rounded-full"
                    data-testid="suggestion-not-improving-btn"
                  >
                    Find the Root
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Distress level — inline ONLY when there's no player on this node.
              When a player is present, the check-in is shown as a floating
              forced-choice modal after the bilateral sound wraps. */}
          {task.hasDistressSlider && !task.hasPlayer && (
            <div className="mb-6">
              <DistressChecker
                label={task.distressPrompt || "How distressed do you feel now?"}
                value={distressAfter}
                onChange={setDistressAfter}
                testId="distress-after"
              />
              {celebratingSud !== null && (
                <div
                  className="mt-4 flex justify-center animate-fade-in-up"
                  data-testid="distress-celebration"
                >
                  <div
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm shadow-[0_6px_24px_rgba(139,168,136,0.25)]"
                    style={{
                      background: celebratingSud === 0
                        ? "linear-gradient(135deg, #3090A8, #9AC7A0)"
                        : "linear-gradient(135deg, #78C0D8, #AFC7F0)",
                      color: "white"
                    }}
                  >
                    <Sparkles className="w-4 h-4" />
                    {celebratingSud === 0
                      ? "You made it to 0 — that's huge!"
                      : "Down to 1 — brilliant!"}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Follow-up Yes/No question — inline ONLY when there's no player here.
              With a player, the Yes/No lives in the floating modal below. */}
          {task.branches?.kind === "distress-question-gate"
            && !task.hasPlayer
            && distressAfter !== null
            && distressAfter <= (task.branches.threshold ?? 2) && (
            <div className="mb-8 bg-[#FBF8F1] rounded-2xl p-5 border border-slate-100 animate-fade-in-up" data-testid="follow-up-question">
              <p className="text-[#1B2349] font-medium mb-3">{task.branches.question}</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setAnswerYes(true)}
                  className={`flex-1 rounded-xl px-4 py-3 text-sm font-medium transition-colors border-2 ${
                    answerYes === true
                      ? "bg-[#3090A8] border-[#3090A8] text-white"
                      : "bg-white border-slate-200 text-[#1B2349] hover:border-[#3090A8]"
                  }`}
                  data-testid="answer-yes-btn"
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setAnswerYes(false)}
                  className={`flex-1 rounded-xl px-4 py-3 text-sm font-medium transition-colors border-2 ${
                    answerYes === false
                      ? "bg-[#78C0D8] border-[#78C0D8] text-white"
                      : "bg-white border-slate-200 text-[#1B2349] hover:border-[#78C0D8]"
                  }`}
                  data-testid="answer-no-btn"
                >
                  No
                </button>
              </div>
            </div>
          )}

          {/* "I'm Not Improving" button — shown on nodes that opt in */}
          {task.showNotImproving && (
            <div className="mb-8 pt-4 border-t border-slate-100">
              <Button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/not-improving`, {
                      intervention_type: type,
                      distress_after: distressAfter
                    }, { withCredentials: true });
                  } catch (error) {
                    logger.error("Failed to track not-improving:", error);
                  }
                  navigate(`/intervention/${type}/not-improving`);
                }}
                variant="outline"
                className="w-full rounded-xl border-[#E2B887] text-[#B8965A] hover:bg-[#FEF3E7] hover:text-[#96773A] py-3"
                data-testid="not-improving-btn"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                I'm Not Improving
              </Button>
            </div>
          )}

          {/* Input area for tasks that ask for reflection */}
          {task.hasInput && (
            <div className="mb-6">
              <label className="block text-[#1B2349] font-medium mb-2">
                {task.inputPrompt}
              </label>
              <Textarea
                value={taskInput}
                onChange={(e) => setTaskInput(e.target.value)}
                placeholder="Take your time to reflect and write..."
                className="min-h-[150px] rounded-xl border-slate-200 focus:border-[#3090A8] focus:ring-[#3090A8]/30"
                data-testid="task-input"
              />
            </div>
          )}
          </div>
        )}
      </main>

      {/* Hands-free control pill — floats above the bottom nav whenever
          Play-all mode is anything other than 'off'. Shows the current
          state and a one-tap toggle to stop or resume. Marked
          data-hands-free-safe so tapping it doesn't itself trigger the
          "any interaction pauses hands-free" guard. */}
      {handsFreeState !== "off" && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-24 z-40 max-w-[92%]"
          data-hands-free-safe
          data-testid="hands-free-pill"
        >
          <div className={`flex items-center gap-3 px-4 py-2.5 rounded-full shadow-lg border border-white/10 transition-colors ${
            handsFreeState === "waiting-for-input"
              ? "bg-[#C6884A] text-white"
              : "bg-[#1B2349] text-white"
          }`}>
            {handsFreeState === "running" ? (
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#78C0D8] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#78C0D8]"></span>
              </span>
            ) : handsFreeState === "paused" ? (
              <Pause className="w-3.5 h-3.5 text-[#FEF3E7]" />
            ) : (
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
              </span>
            )}
            <span className="text-sm font-medium">
              {handsFreeState === "running"
                ? "Hands-free"
                : handsFreeState === "paused"
                ? "Paused"
                : (task?.hasDistressSlider || task?.hasInput
                    ? "Move the slider, then tap Continue"
                    : "Read this, then tap Continue")}
            </span>
            {handsFreeState === "paused" && (
              <button
                type="button"
                onClick={resumeHandsFree}
                className="text-xs font-medium px-3 py-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
                data-testid="hands-free-resume-btn"
              >
                Resume
              </button>
            )}
            <button
              type="button"
              onClick={stopHandsFree}
              className="text-xs font-medium px-3 py-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
              data-testid="hands-free-stop-btn"
            >
              Stop
            </button>
          </div>
        </div>
      )}

      {/* Fixed Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 p-4 pr-20 sm:pr-4" data-hands-free-safe>
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <Button
            onClick={handlePreviousTask}
            variant="outline"
            disabled={
              saving
              || (nodeHistory.length === 0 && currentNodeId === "task-1")
            }
            className="flex-1 sm:flex-none rounded-full"
            data-testid="prev-task-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          <Link
            to="/dashboard"
            className="hidden sm:flex items-center gap-2 text-[#718096] hover:text-[#1B2349] transition-colors"
          >
            <Home className="w-4 h-4" />
            Dashboard
          </Link>

          {(() => {
            // Determine if Next/Continue is ready.
            const needsSlider = task.hasDistressSlider;
            const needsYesNo = task.branches?.kind === "distress-question-gate"
              && distressAfter !== null
              && distressAfter <= (task.branches.threshold ?? 2);
            const sliderOk = !needsSlider || distressAfter !== null;
            const yesNoOk = !needsYesNo || answerYes !== null;
            const ready = sliderOk && yesNoOk;
            const isEnd = task.branches?.kind === "end";
            // Show the celebration overlay for transitions after tasks
            // 2-7 (i.e. before tasks 3-8). Skip on task-1 (intro) and
            // task-8 (last inner task; task-9 is the wrap-up celebration
            // and doesn't need another overlay before it).
            const shouldCelebrate =
              !isEnd &&
              typeof node?.taskNumber === "number" &&
              node.taskNumber >= 2 &&
              node.taskNumber <= 7;
            const onContinueClick = () => {
              if (shouldCelebrate) {
                setPendingCelebrationTask(node.taskNumber);
              } else {
                handleNextTask();
              }
            };
            return (
              <Button
                onClick={onContinueClick}
                disabled={!ready || saving}
                className="flex-1 sm:flex-none btn-primary"
                data-testid="next-task-btn"
              >
                {isEnd ? 'Complete' : 'Continue'}
                {!isEnd && <ChevronRight className="w-4 h-4 ml-2" />}
              </Button>
            );
          })()}
        </div>
      </div>

      {/* Floating completion summary — auto-opens on task 9 */}
      <CompletionSummary
        open={showCompletionSummary}
        onClose={() => setShowCompletionSummary(false)}
        interventionType={type}
        interventionTitle={INTERVENTION_TITLES[type]}
        readings={buildReadings()}
      />

      {/* "Is this practice right for you?" intro card — shown the first
          time a user opens this intervention. Dismissed permanently per
          intervention via localStorage so it never gets in the way of
          power users returning to a familiar practice. */}
      <Dialog
        open={showIntro}
        onOpenChange={(o) => !o && setShowIntro(false)}
      >
        <DialogContent
          className="bg-white rounded-3xl border-0 shadow-2xl p-7 sm:p-8 max-w-md"
          data-testid="practice-intro-card"
        >
          <DialogTitle className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] tracking-tight leading-tight">
            {getPracticeIntro(type).headline}
          </DialogTitle>
          <DialogDescription className="text-[#4A5568] text-base leading-relaxed mt-3">
            {getPracticeIntro(type).body}
          </DialogDescription>
          {/* Time expectation — same copy for every practice, pulled from
              practiceIntros.PRACTICE_TIME_NOTE so it stays consistent
              across the modal + marketing detail page. */}
          <p
            className="text-[#4A5568] text-sm leading-relaxed mt-3 italic"
            data-testid="practice-intro-time-note"
          >
            {PRACTICE_TIME_NOTE}
          </p>
          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              onClick={() => {
                try { localStorage.setItem(introStorageKey, "1"); } catch (_) { /* noop */ }
                setShowIntro(false);
              }}
              className="rounded-full bg-[#1B2349] text-white hover:bg-[#0F1838] font-medium py-3"
              data-testid="practice-intro-start-btn"
            >
              Let's get started
            </Button>
            <Button
              onClick={() => {
                setShowIntro(false);
                navigate("/dashboard");
              }}
              variant="outline"
              className="rounded-full border-slate-300 text-[#1B2349] hover:bg-slate-50 font-medium py-3"
              data-testid="practice-intro-back-btn"
            >
              Go back
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating check-in modal — appears after the bilateral sound finishes
          when the node expects a distress reading and/or a Yes/No answer.
          Keeps the page body uncluttered; the forced-choice interaction lives
          in one focused overlay. */}
      <CheckInModal
        open={showCheckInModal}
        onClose={() => setShowCheckInModal(false)}
        onContinue={() => {
          setShowCheckInModal(false);
          // Mirror the inline Continue button's celebration logic — tasks
          // 2-7 route through the floating step-completion card first.
          // Defer a hair so the dialog exit animation doesn't stutter
          // with the celebration overlay's entrance animation.
          const shouldCelebrate =
            task?.branches?.kind !== "end" &&
            typeof node?.taskNumber === "number" &&
            node.taskNumber >= 2 &&
            node.taskNumber <= 7;
          if (shouldCelebrate) {
            setTimeout(() => setPendingCelebrationTask(node.taskNumber), 150);
          } else {
            setTimeout(handleNextTask, 150);
          }
        }}
        needsSlider={!!task.hasDistressSlider}
        distressPrompt={task.distressPrompt || "How distressed do you feel now?"}
        distressValue={distressAfter}
        onDistressChange={setDistressAfter}
        needsYesNo={
          task.branches?.kind === "distress-question-gate"
          && distressAfter !== null
          && distressAfter <= (task.branches?.threshold ?? 2)
        }
        questionPrompt={task.branches?.question}
        answerYes={answerYes}
        onYesNoChange={setAnswerYes}
        celebratingSud={celebratingSud}
        saving={saving}
      />
    </div>
  );
};

// Floating forced-choice modal rendered after each bilateral sound finishes.
// Pulls the distress slider, Yes/No follow-up, celebration pill, and Continue
// CTA into one focused overlay — reduces the cognitive load of a long task
// page and makes the check-in feel like a deliberate pause rather than a
// scroll destination.
const CheckInModal = ({
  open,
  onClose,
  onContinue,
  needsSlider,
  distressPrompt,
  distressValue,
  onDistressChange,
  needsYesNo,
  questionPrompt,
  answerYes,
  onYesNoChange,
  celebratingSud,
  saving,
}) => {
  const canContinue =
    (!needsSlider || distressValue !== null)
    && (!needsYesNo || answerYes !== null);

  return (
    <Dialog open={open} onOpenChange={() => { /* forced choice — can't dismiss without answering */ }}>
      <DialogContent
        className="sm:max-w-lg rounded-3xl p-0 overflow-hidden border-0 shadow-[0_30px_80px_rgba(0,0,0,0.18)]"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="checkin-modal"
      >
        {/* Warm gradient header — carries the full prompt so the white body
            stays focused on the slider interaction. */}
        <div className="relative px-6 pt-7 pb-6 bg-gradient-to-br from-[#3090A8] via-[#9DB8A0] to-[#78C0D8] text-white overflow-hidden">
          <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <DialogTitle className="font-outfit font-semibold text-xl sm:text-2xl leading-tight">
              How did that feel?
            </DialogTitle>
            <DialogDescription className="text-white/90 text-sm sm:text-[15px] leading-relaxed mt-2.5 space-y-2.5">
              <span className="block">
                You might notice tension in your body (heavy chest, tight muscles, a lump in your throat, tears) or in your emotions (sadness, anger, worry, shame).
              </span>
              <span className="block">
                Take a moment — tap the scale below to check in with yourself.
              </span>
            </DialogDescription>
          </div>
        </div>

        {/* Body — slider only, no duplicated text */}
        <div className="px-6 py-6 bg-white">
          {needsSlider && (
            <DistressChecker
              label={distressPrompt}
              hideLabel
              value={distressValue}
              onChange={onDistressChange}
              testId="modal-distress-after"
            />
          )}

          {/* Celebration pill inside the modal so it lands where the eye is */}
          {celebratingSud !== null && (
            <div className="mt-4 flex justify-center animate-fade-in-up">
              <div
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm shadow-[0_6px_24px_rgba(139,168,136,0.25)]"
                style={{
                  background: celebratingSud === 0
                    ? "linear-gradient(135deg, #3090A8, #9AC7A0)"
                    : "linear-gradient(135deg, #78C0D8, #AFC7F0)",
                  color: "white"
                }}
                data-testid="modal-distress-celebration"
              >
                <Sparkles className="w-4 h-4" />
                {celebratingSud === 0
                  ? "You made it to 0 — that's huge!"
                  : "Down to 1 — brilliant!"}
              </div>
            </div>
          )}

          {/* Yes/No follow-up, only once the slider qualifies */}
          {needsYesNo && questionPrompt && (
            <div className="mt-6 pt-5 border-t border-slate-100 animate-fade-in-up">
              <p className="text-[#1B2349] leading-relaxed mb-3 text-center">
                {questionPrompt}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant={answerYes === true ? "default" : "outline"}
                  onClick={() => onYesNoChange(true)}
                  className={`rounded-full ${answerYes === true ? "bg-[#3090A8] hover:bg-[#7A9778] text-white" : "border-[#3090A8] text-[#3090A8] hover:bg-[#3090A8] hover:text-white"}`}
                  data-testid="modal-answer-yes"
                >
                  Yes
                </Button>
                <Button
                  variant={answerYes === false ? "default" : "outline"}
                  onClick={() => onYesNoChange(false)}
                  className={`rounded-full ${answerYes === false ? "bg-[#78C0D8] hover:bg-[#6B8ACD] text-white" : "border-[#78C0D8] text-[#78C0D8] hover:bg-[#78C0D8] hover:text-white"}`}
                  data-testid="modal-answer-no"
                >
                  Not yet
                </Button>
              </div>
            </div>
          )}

          <div className="mt-7 flex justify-end">
            <Button
              onClick={onContinue}
              disabled={!canContinue || saving}
              className="bg-[#3090A8] hover:bg-[#7A9778] text-white rounded-full px-6"
              data-testid="modal-continue-btn"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InterventionFlow;
